/**
 * SPA shell: login (BFF cookie) → devices / PATs / MCP setup.
 * Send settings expand inline on each device card. Invite accept flow:
 * /invite/accept?token=… stores token in sessionStorage, preview pre-login,
 * accept after session. Cognito tokens never touch localStorage.
 */

import * as api from "./api.js";
import { mcpSetupConfig } from "./config.js";
import { clear, copyText, el, formatDate, withShowPassword } from "./dom.js";
import {
  ApiError,
  DEFAULT_FEATURE_FLAGS,
  isAdminRole,
  isLoginChallenge,
  scopesOf,
  type AdminFlag,
  type AdminInvite,
  type AdminUser,
  type AuditEvent,
  type FeatureFlags,
  type FlagStatus,
  type InvitePreview,
  type InviteStatus,
  type McpClient,
  type MintedPat,
  type PatMeta,
  type PatScope,
  type UserProfile,
  type UserRole,
} from "./types.js";
import {
  ALLOW_CONTACTS_SENDS_WARNING,
  AUTONOMOUS_SENDS_WARNING,
  findPossibleDuplicateDevice,
  formatCanonicalizedEntries,
  formatCountdown,
  isExpired,
  isValidAccountLabel,
  MAX_ACCOUNT_LABEL_LENGTH,
  parseAllowlistText,
  type CanonicalizedEntry,
  type PairingCode,
  type PublicAccount,
  type PublicDevice,
} from "./chat.js";

/** sessionStorage key for pre-login invite token (never localStorage). */
export const INVITE_TOKEN_STORAGE_KEY = "chat_invite_token";
/** Raw invite tokens are prefixed chat_inv_ server-side. */
const INVITE_RAW_PREFIX = "chat_inv_";

type ChallengeState =
  | { kind: "NEW_PASSWORD_REQUIRED"; session: string; email: string }
  | { kind: "SOFTWARE_TOKEN_MFA"; session: string; email: string }
  | null;

/** Unauthenticated password-reset UI (SPA BFF → Cognito ForgotPassword). */
type ForgotPasswordState =
  | { step: "request" }
  | { step: "confirm"; email: string }
  | null;

/** Unauthenticated public self-signup (gated by auth.selfSignup). */
type SignUpState =
  | { step: "register" }
  | { step: "confirm"; email: string }
  | null;

type InviteFlowState = {
  /** Raw chat_inv_… held only in memory + sessionStorage. */
  token: string | null;
  preview: InvitePreview | null;
  /** Accept succeeded this session. */
  accepted: boolean;
  /** Preview/accept in progress. */
  loading: boolean;
};

type AppState = {
  user: UserProfile | null;
  challenge: ChallengeState;
  /** Forgot-password panel when signed out (not a Cognito login challenge). */
  forgotPassword: ForgotPasswordState;
  /** Public self-signup panel when signed out. */
  signUp: SignUpState;
  /** Paired Macs. */
  devices: PublicDevice[];
  /** Current Mac adapter download (from GET /api/v1/adapter). */
  adapterDownload: api.AdapterDownloadInfo | null;
  /** iMessage accounts, one per paired device. */
  accounts: PublicAccount[];
  /** In-flight pairing code (minted, not yet claimed / expired). */
  pairing: PairingCode | null;
  /** Set once a newly-paired or newly-online device is detected while pairing. */
  pairingSuccessDevice: string | null;
  /** Device ids known at the moment pairing started (to detect new devices). */
  pairingBaselineIds: Set<string>;
  /** Device ids that were already online at the moment pairing started. */
  pairingBaselineOnline: Set<string>;
  /** Last save result per account id (pushed + canonicalized rewrites). */
  accountSaveNotice: Record<
    string,
    { pushed: boolean; canonicalized: CanonicalizedEntry[] } | null
  >;
  /**
   * Device id whose Send settings panel is expanded inline (null = collapsed).
   * Kept in state so save/busy re-renders keep the panel open.
   */
  sendSettingsDeviceId: string | null;
  tokens: PatMeta[];
  /** MCP OAuth + PAT connections (Active clients list). */
  mcpClients: McpClient[];
  minted: MintedPat | null;
  features: FeatureFlags;
  /** Own audit events (GET /api/v1/audit). */
  auditEvents: AuditEvent[];
  auditNextCursor: string | null;
  /** @deprecated Activity is a full page now; kept false unused. */
  auditExpanded: boolean;
  /** Top-right profile dropdown open. */
  profileMenuOpen: boolean;
  /** Admin anomaly flags (GET /api/v1/admin/flags) — owner/admin only. */
  adminFlags: AdminFlag[];
  adminFlagsNextCursor: string | null;
  adminFlagsStatusFilter: FlagStatus | "all";
  /** Admin invites (GET /api/v1/admin/invites) — requires invites.productized. */
  adminInvites: AdminInvite[];
  adminInviteStatusFilter: InviteStatus | "all";
  /** One-time invite URL from create/resend (shown until dismissed/copied). */
  lastInviteLink: { email: string; inviteUrl: string } | null;
  /** Admin users (GET /api/v1/admin/users). */
  adminUsers: AdminUser[];
  /** Active section on /admin ribbon: users | invites | flags. */
  adminTab: "users" | "invites" | "flags";
  /** Client-side + server search for Admin · Users. */
  adminUserQuery: string;
  adminUserRoleFilter: UserRole | "all";
  adminUserStatusFilter: "active" | "disabled" | "all";
  /**
   * Selected user activity trail (GET …/admin/users/:id/audit).
   * null when panel closed.
   */
  adminUserActivity: {
    userId: string;
    email: string;
    events: AuditEvent[];
    nextCursor: string | null;
  } | null;
  loading: boolean;
  busy: string | null;
  error: string | null;
  notice: string | null;
  invite: InviteFlowState;
};

const state: AppState = {
  user: null,
  challenge: null,
  forgotPassword: null,
  signUp: null,
  devices: [],
  adapterDownload: null,
  accounts: [],
  pairing: null,
  pairingSuccessDevice: null,
  pairingBaselineIds: new Set(),
  pairingBaselineOnline: new Set(),
  accountSaveNotice: {},
  sendSettingsDeviceId: null,
  tokens: [],
  mcpClients: [],
  minted: null,
  features: { ...DEFAULT_FEATURE_FLAGS },
  auditEvents: [],
  auditNextCursor: null,
  auditExpanded: false,
  profileMenuOpen: false,
  adminFlags: [],
  adminFlagsNextCursor: null,
  adminFlagsStatusFilter: "open",
  adminInvites: [],
  adminInviteStatusFilter: "pending",
  lastInviteLink: null,
  adminUsers: [],
  adminTab: "users",
  adminUserQuery: "",
  adminUserRoleFilter: "all",
  adminUserStatusFilter: "all",
  adminUserActivity: null,
  loading: true,
  busy: null,
  error: null,
  notice: null,
  invite: {
    token: null,
    preview: null,
    accepted: false,
    loading: false,
  },
};

const AUDIT_PAGE_SIZE = 25;
const FLAGS_PAGE_SIZE = 25;
/** Background refresh of the devices list while the Devices panel is mounted. */
const DEVICES_POLL_MS = 10000;
/**
 * Faster poll when any online device reports messagesRunning=false so the
 * warning clears promptly after Messages.app is opened (adapter status is
 * ~30s; SPA was only re-fetching every 10s).
 */
const DEVICES_POLL_MS_WARN = 4000;
/** Tighter poll while a pairing code is on screen, waiting for the Mac to claim it. */
const PAIRING_POLL_MS = 2500;

let root: HTMLElement;

// Module-level timer handles — this app has no component unmount lifecycle,
// so polling is guarded by these instead (start once, never double-start).
let devicesPollTimer: ReturnType<typeof setInterval> | null = null;
let pairingPollTimer: ReturnType<typeof setInterval> | null = null;
let pairingTickTimer: ReturnType<typeof setInterval> | null = null;

export function mount(container: HTMLElement): void {
  root = container;
  if (typeof window !== "undefined") {
    window.addEventListener("popstate", () => {
      state.profileMenuOpen = false;
      render();
    });
  }
  wireProfileMenuDismiss();
  void boot();
}

function readSessionInviteToken(): string | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const t = sessionStorage.getItem(INVITE_TOKEN_STORAGE_KEY);
    return t && t.startsWith(INVITE_RAW_PREFIX) ? t : null;
  } catch {
    return null;
  }
}

function writeSessionInviteToken(token: string): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(INVITE_TOKEN_STORAGE_KEY, token);
  } catch {
    // private mode / quota — keep in-memory only
  }
}

function clearSessionInviteToken(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(INVITE_TOKEN_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Capture invite token from /invite/accept?token=… into sessionStorage,
 * strip token from the URL (avoid history leakage), keep path for UI.
 */
function consumeInviteQuery(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const path = url.pathname.replace(/\/+$/, "") || "/";
    const isAcceptPath =
      path === "/invite/accept" || path.endsWith("/invite/accept");
    const qToken = url.searchParams.get("token");
    if (qToken && qToken.startsWith(INVITE_RAW_PREFIX)) {
      writeSessionInviteToken(qToken);
      state.invite.token = qToken;
      url.searchParams.delete("token");
      const cleanPath = isAcceptPath ? "/invite/accept" : path;
      const clean =
        cleanPath +
        (url.searchParams.toString() ? `?${url.searchParams}` : "") +
        url.hash;
      window.history.replaceState({}, "", clean);
    } else if (isAcceptPath || readSessionInviteToken()) {
      state.invite.token = readSessionInviteToken();
    }
  } catch {
    // ignore
  }
}

function isInviteAcceptRoute(): boolean {
  if (typeof window === "undefined") return !!state.invite.token;
  try {
    const path = new URL(window.location.href).pathname.replace(/\/+$/, "") || "/";
    return (
      path === "/invite/accept" ||
      path.endsWith("/invite/accept") ||
      !!state.invite.token
    );
  } catch {
    return !!state.invite.token;
  }
}

async function loadInvitePreview(): Promise<void> {
  const token = state.invite.token;
  if (!token) return;
  state.invite.loading = true;
  try {
    const preview = await api.previewInvite(token);
    state.invite.preview = preview;
  } catch (err) {
    state.invite.preview = null;
    setError(inviteErrorMessage(err));
  } finally {
    state.invite.loading = false;
  }
}

/**
 * After session login, bind JWT/session sub via POST accept.
 * Idempotent same-sub; mismatch → 401; expired → 410; other sub → 409.
 */
async function tryAcceptPendingInvite(): Promise<boolean> {
  const token = state.invite.token ?? readSessionInviteToken();
  if (!token || !state.user) return false;
  state.invite.token = token;
  setBusy("invite-accept");
  try {
    const res = await api.acceptInvite(token);
    if (res.status === "pending_login") {
      // Session missing on server — keep token for retry.
      return false;
    }
    state.invite.accepted = true;
    clearSessionInviteToken();
    state.invite.token = null;
    setNotice(`Invite accepted for ${res.email}. Welcome!`);
    setError(null);
    // Leave /invite/accept path for a clean dashboard URL.
    if (typeof window !== "undefined") {
      try {
        window.history.replaceState({}, "", "/");
      } catch {
        // ignore
      }
    }
    return true;
  } catch (err) {
    setError(inviteErrorMessage(err));
    return false;
  } finally {
    setBusy(null);
  }
}

function inviteErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) {
      return "Signed-in email does not match this invite. Sign out and use the invited address.";
    }
    if (err.status === 410) {
      return err.message || "This invite has expired or was revoked.";
    }
    if (err.status === 409) {
      return err.message || "This invite was already accepted by another account.";
    }
    if (err.status === 404) {
      return "Invite link is invalid or has already been used.";
    }
    return err.message || err.code;
  }
  return errMessage(err);
}

async function boot(): Promise<void> {
  state.loading = true;
  state.error = null;
  consumeInviteQuery();
  render();

  // Public flags (auth.selfSignup, etc.) for pre-login UI.
  state.features = await api.tryGetFeatures();

  // Pre-login preview (safe metadata) when we have a token.
  if (state.invite.token) {
    await loadInvitePreview();
  }

  try {
    const me = await api.getMe();
    state.user = me;
    if (state.invite.token) {
      await tryAcceptPendingInvite();
    }
    await refreshDashboard();
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      state.user = null;
    } else {
      // Network / server down — show login; surface soft message
      state.user = null;
      if (!(err instanceof ApiError && err.status === 401)) {
        state.notice = errMessage(err);
      }
    }
  } finally {
    state.loading = false;
    render();
  }
}

async function refreshDashboard(): Promise<void> {
  const [devicesRes, tokens, mcpClients, features, auditPage, adapterDownload] =
    await Promise.all([
      api.listDevices(),
      api.listTokens(),
      api.listMcpClients().catch(() => [] as McpClient[]),
      api.tryGetFeatures(),
      api.listAudit({ limit: AUDIT_PAGE_SIZE }),
      api.getAdapterDownload(),
    ]);
  state.devices = devicesRes.devices;
  state.accounts = devicesRes.accounts;
  state.adapterDownload = adapterDownload;
  state.tokens = tokens;
  state.mcpClients = mcpClients;
  state.features = features;
  state.auditEvents = auditPage.events;
  state.auditNextCursor = auditPage.nextCursor ?? null;

  ensureDevicesPolling();

  if (state.user && isAdminRole(state.user.role)) {
    await refreshAdminData();
  } else {
    state.adminFlags = [];
    state.adminFlagsNextCursor = null;
    state.adminInvites = [];
    state.adminUsers = [];
    state.lastInviteLink = null;
  }
}

/** Load admin-only lists (invites, users, flags). Soft-fails per section. */
async function refreshAdminData(): Promise<void> {
  // Users always available to admin (not gated by invites.productized).
  try {
    state.adminUsers = await api.listAdminUsers();
  } catch (err) {
    state.adminUsers = [];
    if (err instanceof ApiError && err.status !== 403) {
      setNotice(`Admin users unavailable: ${errMessage(err)}`);
    }
  }

  // Invites require invites.productized.
  if (state.features["invites.productized"]) {
    try {
      state.adminInvites = await api.listAdminInvites();
    } catch (err) {
      state.adminInvites = [];
      if (err instanceof ApiError && err.status !== 403) {
        setNotice(`Admin invites unavailable: ${errMessage(err)}`);
      }
    }
  } else {
    state.adminInvites = [];
  }

  try {
    const status =
      state.adminFlagsStatusFilter === "all"
        ? undefined
        : state.adminFlagsStatusFilter;
    const flagsPage = await api.listAdminFlags({
      limit: FLAGS_PAGE_SIZE,
      ...(status ? { status } : {}),
    });
    state.adminFlags = flagsPage.flags;
    state.adminFlagsNextCursor = flagsPage.nextCursor ?? null;
  } catch (err) {
    state.adminFlags = [];
    state.adminFlagsNextCursor = null;
    if (err instanceof ApiError && err.status !== 403) {
      setNotice(`Admin flags unavailable: ${errMessage(err)}`);
    }
  }
}

function errMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return err.message || err.code;
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}

function setBusy(key: string | null): void {
  state.busy = key;
  render();
}

function setError(msg: string | null): void {
  state.error = msg;
}

function setNotice(msg: string | null): void {
  state.notice = msg;
}

/** Client-side admin dashboard route. */
export function isAdminPath(
  pathname: string = typeof window !== "undefined"
    ? window.location.pathname
    : "/",
): boolean {
  const p = pathname.replace(/\/+$/, "") || "/";
  return p === "/admin";
}

function currentPath(): string {
  if (typeof window === "undefined") return "/";
  return window.location.pathname.replace(/\/+$/, "") || "/";
}

function isProfileRoute(): boolean {
  return currentPath() === "/profile";
}

function isActivityRoute(): boolean {
  return currentPath() === "/activity";
}

function navigateTo(path: string): void {
  if (typeof window === "undefined") return;
  state.profileMenuOpen = false;
  const next = path.startsWith("/") ? path : `/${path}`;
  if (window.location.pathname !== next) {
    window.history.pushState({}, "", next);
  }
  render();
}

let profileMenuDismissWired = false;
function wireProfileMenuDismiss(): void {
  if (profileMenuDismissWired || typeof document === "undefined") return;
  profileMenuDismissWired = true;
  document.addEventListener("click", (ev) => {
    if (!state.profileMenuOpen) return;
    const t = ev.target;
    if (!(t instanceof Node)) return;
    const menu = document.querySelector(".profile-menu");
    if (menu && menu.contains(t)) return;
    state.profileMenuOpen = false;
    render();
  });
  document.addEventListener("keydown", (ev) => {
    if (ev.key !== "Escape") return;
    if (state.sendSettingsDeviceId) {
      collapseSendSettings();
      return;
    }
    if (!state.profileMenuOpen) return;
    state.profileMenuOpen = false;
    render();
  });
}

// ── Render ────────────────────────────────────────────────────────────────

function render(): void {
  clear(root);
  if (state.loading) {
    root.append(
      el(
        "div",
        { className: "boot" },
        el("div", { className: "spinner" }),
        el("div", null, "Checking session…"),
      ),
    );
    return;
  }
  if (!state.user) {
    // Not signed in: /admin still needs login first.
    root.append(renderLogin());
    return;
  }
  if (isAdminPath()) {
    root.append(renderAdminShell());
    return;
  }
  root.append(renderShell());
}

function renderLogin(): HTMLElement {
  const wrap = el("div", { className: "login-wrap" });
  const card = el("div", { className: "login-card" });

  card.append(
    el(
      "div",
      { className: "brand" },
      el("div", { className: "brand-mark", textContent: "M" }),
      el("span", null, "MCP to Chat"),
    ),
  );

  if (state.challenge?.kind === "NEW_PASSWORD_REQUIRED") {
    card.append(
      el("h1", null, "Set a new password"),
      el(
        "p",
        { className: "lede" },
        "Your temporary password must be replaced before you can continue.",
      ),
    );
    if (isInviteAcceptRoute() && state.invite.preview) {
      card.append(inviteBanner());
    }
    if (state.error) card.append(alertBox("error", state.error));
    card.append(newPasswordForm(state.challenge.email, state.challenge.session));
  } else if (state.challenge?.kind === "SOFTWARE_TOKEN_MFA") {
    card.append(
      el("h1", null, "Two-factor authentication"),
      el("p", { className: "lede" }, "Enter the code from your authenticator app."),
    );
    if (state.error) card.append(alertBox("error", state.error));
    card.append(totpForm(state.challenge.email, state.challenge.session));
  } else if (state.forgotPassword?.step === "request") {
    card.append(
      el("h1", null, "Forgot password"),
      el(
        "p",
        { className: "lede" },
        "Enter your account email. If it exists, we will send a verification code.",
      ),
    );
    if (state.notice) card.append(alertBox("info", state.notice));
    if (state.error) card.append(alertBox("error", state.error));
    card.append(forgotPasswordRequestForm());
  } else if (state.forgotPassword?.step === "confirm") {
    card.append(
      el("h1", null, "Reset password"),
      el(
        "p",
        { className: "lede" },
        `Enter the code we emailed to ${state.forgotPassword.email} and choose a new password.`,
      ),
    );
    if (state.notice) card.append(alertBox("info", state.notice));
    if (state.error) card.append(alertBox("error", state.error));
    card.append(forgotPasswordConfirmForm(state.forgotPassword.email));
  } else if (state.signUp?.step === "register") {
    card.append(
      el("h1", null, "Create account"),
      el(
        "p",
        { className: "lede" },
        "Sign up with email. We’ll send a verification code before you can sign in.",
      ),
    );
    if (state.notice) card.append(alertBox("info", state.notice));
    if (state.error) card.append(alertBox("error", state.error));
    card.append(signUpRegisterForm());
  } else if (state.signUp?.step === "confirm") {
    card.append(
      el("h1", null, "Confirm email"),
      el(
        "p",
        { className: "lede" },
        `Enter the code we emailed to ${state.signUp.email}.`,
      ),
    );
    if (state.notice) card.append(alertBox("info", state.notice));
    if (state.error) card.append(alertBox("error", state.error));
    card.append(signUpConfirmForm(state.signUp.email));
  } else if (isInviteAcceptRoute()) {
    card.append(
      el("h1", null, "Accept your invite"),
      el(
        "p",
        { className: "lede" },
        "Sign in with the invited email. After login we bind your account to this invite.",
      ),
    );
    card.append(inviteBanner());
    if (state.notice) card.append(alertBox("info", state.notice));
    if (state.error) card.append(alertBox("error", state.error));
    if (state.invite.loading) {
      card.append(el("p", { className: "hint" }, "Checking invite…"));
    } else if (state.invite.token) {
      card.append(loginForm({ prefillEmail: state.invite.preview?.email }));
    } else {
      card.append(
        alertBox("error", "Missing invite token. Open the full link from your email."),
      );
    }
  } else {
    const selfSignup = state.features["auth.selfSignup"] === true;
    card.append(
      el("h1", null, "Sign in"),
      el(
        "p",
        { className: "lede" },
        selfSignup
          ? "Sign in with your email, or create an account if you’re new."
          : "Sign in with your account email. Open your invite link if this is your first login.",
      ),
    );
    if (state.notice) card.append(alertBox("info", state.notice));
    if (state.error) card.append(alertBox("error", state.error));
    card.append(loginForm({ showCreateAccount: selfSignup }));
  }

  wrap.append(card);
  return wrap;
}

function inviteBanner(): HTMLElement {
  const box = el("div", {
    className: "invite-banner",
    role: "status",
  });
  const preview = state.invite.preview;
  if (preview) {
    box.append(
      el(
        "div",
        { className: "invite-banner-title" },
        "Invite for ",
        el("strong", null, preview.email),
      ),
      el(
        "p",
        { className: "hint" },
        [
          preview.role ? `Role: ${preview.role}` : null,
          preview.expiresAt
            ? `Expires ${formatDate(preview.expiresAt)}`
            : null,
        ]
          .filter(Boolean)
          .join(" · ") || "Sign in to accept.",
      ),
    );
  } else if (state.invite.token && !state.invite.loading && !state.error) {
    box.append(el("p", { className: "hint" }, "Invite token ready — sign in to accept."));
  }
  return box;
}

function loginForm(opts?: {
  prefillEmail?: string;
  showCreateAccount?: boolean;
}): HTMLElement {
  const form = el("form", { className: "form-stack", novalidate: true });
  const email = el("input", {
    type: "email",
    name: "email",
    autocomplete: "username",
    required: true,
    placeholder: "you@example.com",
    value: opts?.prefillEmail ?? "",
  }) as HTMLInputElement;
  const password = el("input", {
    type: "password",
    name: "password",
    autocomplete: "current-password",
    required: true,
    placeholder: "Password",
  }) as HTMLInputElement;

  const actions = el("div", { className: "form-actions form-actions-stack" });
  actions.append(
    el(
      "button",
      {
        type: "submit",
        className: "btn-primary",
        disabled: state.busy === "login" ? true : undefined,
      },
      state.busy === "login"
        ? "Signing in…"
        : isInviteAcceptRoute()
          ? "Sign in & accept invite"
          : "Sign in",
    ),
  );

  const forgotBtn = el(
    "button",
    { type: "button", className: "btn-link" },
    "Forgot password?",
  );
  forgotBtn.addEventListener("click", () => {
    state.forgotPassword = { step: "request" };
    state.signUp = null;
    state.challenge = null;
    setError(null);
    setNotice(null);
    render();
  });
  actions.append(forgotBtn);

  if (opts?.showCreateAccount) {
    const createBtn = el(
      "button",
      { type: "button", className: "btn-link" },
      "Create account",
    );
    createBtn.addEventListener("click", () => {
      state.signUp = { step: "register" };
      state.forgotPassword = null;
      state.challenge = null;
      setError(null);
      setNotice(null);
      render();
    });
    actions.append(createBtn);
  }

  form.append(
    el("label", { className: "field" }, "Email", email),
    el("label", { className: "field" }, "Password", withShowPassword(password)),
    actions,
  );

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void onLogin(email.value.trim(), password.value);
  });
  return form;
}

function signUpRegisterForm(): HTMLElement {
  const form = el("form", { className: "form-stack", novalidate: true });
  const email = el("input", {
    type: "email",
    name: "email",
    autocomplete: "username",
    required: true,
    placeholder: "you@example.com",
  }) as HTMLInputElement;
  const password = el("input", {
    type: "password",
    name: "password",
    autocomplete: "new-password",
    required: true,
    minlength: "12",
    placeholder: "Password (12+ chars, mixed case, digit, symbol)",
  }) as HTMLInputElement;
  const password2 = el("input", {
    type: "password",
    name: "password2",
    autocomplete: "new-password",
    required: true,
    minlength: "12",
    placeholder: "Confirm password",
  }) as HTMLInputElement;

  const backBtn = el(
    "button",
    { type: "button", className: "btn-link" },
    "Back to sign in",
  );
  backBtn.addEventListener("click", () => {
    state.signUp = null;
    setError(null);
    setNotice(null);
    render();
  });

  form.append(
    el("label", { className: "field" }, "Email", email),
    el("label", { className: "field" }, "Password", withShowPassword(password)),
    el(
      "label",
      { className: "field" },
      "Confirm password",
      withShowPassword(password2),
    ),
    el(
      "div",
      { className: "form-actions form-actions-stack" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "signup" ? true : undefined,
        },
        state.busy === "signup" ? "Creating…" : "Create account",
      ),
      backBtn,
    ),
  );

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    if (password.value !== password2.value) {
      setError("Passwords do not match.");
      render();
      return;
    }
    void onSignUp(email.value.trim(), password.value);
  });
  return form;
}

function signUpConfirmForm(email: string): HTMLElement {
  const form = el("form", { className: "form-stack", novalidate: true });
  const code = el("input", {
    type: "text",
    name: "code",
    autocomplete: "one-time-code",
    inputMode: "numeric",
    required: true,
    placeholder: "Verification code",
  }) as HTMLInputElement;

  const resendBtn = el(
    "button",
    {
      type: "button",
      className: "btn-link",
      disabled: state.busy === "signup-resend" ? true : undefined,
    },
    state.busy === "signup-resend" ? "Sending…" : "Resend code",
  );
  resendBtn.addEventListener("click", () => {
    void onResendConfirmation(email);
  });

  const backBtn = el(
    "button",
    { type: "button", className: "btn-link" },
    "Back to sign in",
  );
  backBtn.addEventListener("click", () => {
    state.signUp = null;
    setError(null);
    setNotice(null);
    render();
  });

  form.append(
    el("label", { className: "field" }, "Code", code),
    el(
      "div",
      { className: "form-actions form-actions-stack" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "signup-confirm" ? true : undefined,
        },
        state.busy === "signup-confirm" ? "Confirming…" : "Confirm email",
      ),
      resendBtn,
      backBtn,
    ),
  );

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void onConfirmSignUp(email, code.value.trim());
  });
  return form;
}

function forgotPasswordRequestForm(): HTMLElement {
  const form = el("form", { className: "form-stack", novalidate: true });
  const email = el("input", {
    type: "email",
    name: "email",
    autocomplete: "username",
    required: true,
    placeholder: "you@example.com",
  }) as HTMLInputElement;

  form.append(
    el("label", { className: "field" }, "Email", email),
    el(
      "div",
      { className: "form-actions form-actions-stack" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "forgot" ? true : undefined,
        },
        state.busy === "forgot" ? "Sending…" : "Send reset code",
      ),
      el("button", { type: "button", className: "btn-ghost" }, "Back to sign in"),
    ),
  );

  form.querySelector(".btn-ghost")?.addEventListener("click", () => {
    state.forgotPassword = null;
    setError(null);
    setNotice(null);
    render();
  });

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void onForgotPasswordRequest(email.value.trim());
  });
  return form;
}

function forgotPasswordConfirmForm(accountEmail: string): HTMLElement {
  const form = el("form", { className: "form-stack", novalidate: true });
  const code = el("input", {
    type: "text",
    name: "code",
    inputmode: "numeric",
    autocomplete: "one-time-code",
    required: true,
    placeholder: "Verification code",
  }) as HTMLInputElement;
  const pw = el("input", {
    type: "password",
    name: "newPassword",
    autocomplete: "new-password",
    required: true,
    placeholder: "New password",
    minlength: "12",
  }) as HTMLInputElement;
  const confirm = el("input", {
    type: "password",
    name: "confirm",
    autocomplete: "new-password",
    required: true,
    placeholder: "Confirm new password",
  }) as HTMLInputElement;

  form.append(
    el(
      "label",
      { className: "field" },
      "Account",
      el("input", {
        type: "email",
        value: accountEmail,
        disabled: true,
        readonly: true,
      }),
    ),
    el("label", { className: "field" }, "Verification code", code),
    el("label", { className: "field" }, "New password", withShowPassword(pw)),
    el("label", { className: "field" }, "Confirm", withShowPassword(confirm)),
    el(
      "p",
      { className: "hint" },
      "Use at least 12 characters with upper, lower, number, and symbol (pool policy).",
    ),
    el(
      "div",
      { className: "form-actions form-actions-stack" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "forgot" ? true : undefined,
        },
        state.busy === "forgot" ? "Updating…" : "Update password",
      ),
      el(
        "button",
        { type: "button", className: "btn-ghost", "data-action": "resend" },
        "Resend code",
      ),
      el(
        "button",
        { type: "button", className: "btn-ghost", "data-action": "back" },
        "Back to sign in",
      ),
    ),
  );

  form
    .querySelector('[data-action="back"]')
    ?.addEventListener("click", () => {
      state.forgotPassword = null;
      setError(null);
      setNotice(null);
      render();
    });
  form
    .querySelector('[data-action="resend"]')
    ?.addEventListener("click", () => {
      void onForgotPasswordRequest(accountEmail);
    });

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    if (pw.value !== confirm.value) {
      setError("Passwords do not match");
      render();
      return;
    }
    void onForgotPasswordConfirm(accountEmail, code.value.trim(), pw.value);
  });
  return form;
}

function newPasswordForm(email: string, session: string): HTMLElement {
  const form = el("form", { className: "form-stack", novalidate: true });
  const pw = el("input", {
    type: "password",
    name: "newPassword",
    autocomplete: "new-password",
    required: true,
    placeholder: "New password",
    minlength: "8",
  }) as HTMLInputElement;
  const confirm = el("input", {
    type: "password",
    name: "confirm",
    autocomplete: "new-password",
    required: true,
    placeholder: "Confirm new password",
  }) as HTMLInputElement;

  form.append(
    el("label", { className: "field" }, "Account", el("input", {
      type: "email",
      value: email,
      disabled: true,
      readonly: true,
    })),
    el("label", { className: "field" }, "New password", withShowPassword(pw)),
    el("label", { className: "field" }, "Confirm", withShowPassword(confirm)),
    el("p", { className: "hint" }, "Use at least 8 characters. Avoid reusing passwords."),
    el(
      "div",
      { className: "form-actions" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "login" ? true : undefined,
        },
        state.busy === "login" ? "Saving…" : "Set password & sign in",
      ),
      el(
        "button",
        { type: "button", className: "btn-ghost" },
        "Back",
      ),
    ),
  );

  form.querySelector(".btn-ghost")?.addEventListener("click", () => {
    state.challenge = null;
    setError(null);
    render();
  });

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    if (pw.value !== confirm.value) {
      setError("Passwords do not match");
      render();
      return;
    }
    void onNewPassword(email, session, pw.value);
  });
  return form;
}

function totpForm(email: string, session: string): HTMLElement {
  const form = el("form", { className: "form-stack", novalidate: true });
  const code = el("input", {
    type: "text",
    name: "totpCode",
    inputmode: "numeric",
    autocomplete: "one-time-code",
    required: true,
    placeholder: "6-digit code",
    maxlength: "8",
  }) as HTMLInputElement;

  form.append(
    el("label", { className: "field" }, "Authenticator code", code),
    el(
      "div",
      { className: "form-actions" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "login" ? true : undefined,
        },
        state.busy === "login" ? "Verifying…" : "Verify",
      ),
      el("button", { type: "button", className: "btn-ghost" }, "Back"),
    ),
  );

  form.querySelector(".btn-ghost")?.addEventListener("click", () => {
    state.challenge = null;
    setError(null);
    render();
  });

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void onTotp(email, session, code.value.trim());
  });
  return form;
}

function profileInitials(user: UserProfile): string {
  const name = (user.displayName ?? "").trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (
        (parts[0]![0] ?? "") + (parts[parts.length - 1]![0] ?? "")
      ).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  const email = user.email || "?";
  return email.slice(0, 2).toUpperCase();
}

/** Top-right profile control + dropdown (mirrors mcptoemail / mcptoai). */
function profileMenuButton(): HTMLElement {
  const user = state.user!;
  const wrap = el("div", { className: "profile-menu" });

  const trigger = el(
    "button",
    {
      type: "button",
      className: "profile-menu-trigger",
      "aria-haspopup": "menu",
      "aria-expanded": state.profileMenuOpen ? "true" : "false",
      title: user.email,
    },
    el("span", { className: "profile-avatar" }, profileInitials(user)),
    el("span", { className: "profile-trigger-label" }, "Profile"),
  ) as HTMLButtonElement;
  trigger.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    state.profileMenuOpen = !state.profileMenuOpen;
    render();
  });
  wrap.append(trigger);

  if (!state.profileMenuOpen) return wrap;

  const menu = el("div", {
    className: "profile-menu-dropdown",
    role: "menu",
  });
  menu.append(
    el(
      "div",
      { className: "profile-menu-header" },
      el(
        "div",
        { className: "profile-menu-name" },
        user.displayName?.trim() || user.email,
      ),
      el("div", { className: "profile-menu-email" }, user.email),
    ),
  );

  const item = (
    label: string,
    onClick: () => void,
    opts?: { danger?: boolean },
  ): HTMLButtonElement => {
    const btn = el(
      "button",
      {
        type: "button",
        className: opts?.danger
          ? "profile-menu-item profile-menu-item-danger"
          : "profile-menu-item",
        role: "menuitem",
      },
      label,
    ) as HTMLButtonElement;
    btn.addEventListener("click", (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      onClick();
    });
    return btn;
  };

  menu.append(
    item("Edit profile", () => navigateTo("/profile")),
    item("Activity log", () => navigateTo("/activity")),
  );

  if (isAdminRole(user.role)) {
    menu.append(
      item("Admin", () => {
        navigateTo("/admin");
      }),
    );
  }

  menu.append(el("div", { className: "profile-menu-sep" }));
  menu.append(
    item(
      state.busy === "logout" ? "Signing out…" : "Sign out",
      () => {
        void onLogout();
      },
      { danger: true },
    ),
  );

  wrap.append(menu);
  return wrap;
}

function renderTopbar(opts: { adminPage?: boolean } = {}): HTMLElement {
  const top = el("header", { className: "topbar" });
  const brand = el(
    "a",
    {
      href: "/",
      className: "brand brand-link",
      title: "MCP to Chat",
    },
    el("div", { className: "brand-mark", textContent: "M" }),
    el("span", null, "MCP to Chat"),
  );
  brand.addEventListener("click", (ev) => {
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    ev.preventDefault();
    navigateTo("/");
  });
  if (opts.adminPage) {
    brand.append(el("span", { className: "brand-admin-badge" }, "Admin"));
  }

  const meta = el("div", { className: "user-meta" });
  meta.append(profileMenuButton());
  top.append(brand, meta);
  return top;
}

function renderShell(): HTMLElement {
  const shell = el("div", { className: "app-shell" });
  shell.append(renderTopbar());

  if (state.error) shell.append(alertBox("error", state.error));
  if (state.notice) shell.append(alertBox("ok", state.notice));

  // Pending invite while already signed in (e.g. opened link after login).
  if (state.invite.token && !state.invite.accepted) {
    shell.append(pendingInvitePanel());
  }

  if (isProfileRoute()) {
    shell.append(profilePanel());
    return shell;
  }

  if (isActivityRoute()) {
    shell.append(auditPanel());
    return shell;
  }

  shell.append(devicesPanel());
  shell.append(mcpAccessPanel());

  return shell;
}

/** Profile page: display name + change password. */
function profilePanel(): HTMLElement {
  const wrap = el("div", { className: "profile-page" });
  wrap.append(profileIdentityPanel());
  wrap.append(profilePasswordPanel());
  return wrap;
}

function profileIdentityPanel(): HTMLElement {
  const user = state.user!;
  const panel = el("section", { className: "panel" });
  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Edit profile"),
        el(
          "p",
          { className: "sub" },
          "Update how your name appears in the app. Email is your sign-in identity.",
        ),
      ),
    ),
  );

  const form = el("form", { className: "profile-form" }) as HTMLFormElement;
  const nameInput = el("input", {
    type: "text",
    className: "input",
    name: "displayName",
    maxlength: "80",
    autocomplete: "name",
    placeholder: "Display name",
    value: user.displayName ?? "",
  }) as HTMLInputElement;

  form.append(
    el("label", { className: "field" }, "Display name", nameInput),
    el(
      "label",
      { className: "field" },
      "Email",
      el("input", {
        type: "email",
        className: "input",
        value: user.email,
        disabled: "true",
        readonly: "true",
      }),
      el("span", { className: "hint" }, "Email cannot be changed here."),
    ),
    el(
      "label",
      { className: "field" },
      "Role",
      el("input", {
        type: "text",
        className: "input",
        value: user.role,
        disabled: "true",
        readonly: "true",
      }),
    ),
  );

  const actions = el("div", { className: "row-actions" });
  const saveBtn = el(
    "button",
    {
      type: "submit",
      className: "btn-primary btn-sm",
      disabled: state.busy === "profile-save" ? true : undefined,
    },
    state.busy === "profile-save" ? "Saving…" : "Save",
  ) as HTMLButtonElement;
  actions.append(saveBtn);
  form.append(actions);

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void (async () => {
      setBusy("profile-save");
      setError(null);
      setNotice(null);
      render();
      try {
        const updated = await api.updateProfile({
          displayName: nameInput.value,
        });
        state.user = { ...state.user!, ...updated };
        setNotice("Profile updated.");
      } catch (err) {
        setError(errMessage(err));
      } finally {
        setBusy(null);
        render();
      }
    })();
  });

  panel.append(form);
  return panel;
}

function profilePasswordPanel(): HTMLElement {
  const panel = el("section", { className: "panel" });
  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Change password"),
        el(
          "p",
          { className: "sub" },
          "Use your current password, then choose a new one (min 12 characters, with upper, lower, number, and symbol).",
        ),
      ),
    ),
  );

  const form = el("form", { className: "profile-form" }) as HTMLFormElement;
  const currentIn = el("input", {
    type: "password",
    className: "input",
    name: "currentPassword",
    autocomplete: "current-password",
    placeholder: "Current password",
  }) as HTMLInputElement;
  const newIn = el("input", {
    type: "password",
    className: "input",
    name: "newPassword",
    autocomplete: "new-password",
    placeholder: "New password",
    minlength: "12",
  }) as HTMLInputElement;
  const confirmIn = el("input", {
    type: "password",
    className: "input",
    name: "confirmPassword",
    autocomplete: "new-password",
    placeholder: "Confirm new password",
    minlength: "12",
  }) as HTMLInputElement;

  form.append(
    el(
      "label",
      { className: "field" },
      "Current password",
      withShowPassword(currentIn),
    ),
    el(
      "label",
      { className: "field" },
      "New password",
      withShowPassword(newIn),
    ),
    el(
      "label",
      { className: "field" },
      "Confirm new password",
      withShowPassword(confirmIn),
    ),
  );

  const actions = el("div", { className: "row-actions" });
  const saveBtn = el(
    "button",
    {
      type: "submit",
      className: "btn-primary btn-sm",
      disabled: state.busy === "password-change" ? true : undefined,
    },
    state.busy === "password-change" ? "Updating…" : "Update password",
  ) as HTMLButtonElement;
  actions.append(saveBtn);
  form.append(actions);

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const previousPassword = currentIn.value;
    const newPassword = newIn.value;
    const confirm = confirmIn.value;
    if (!previousPassword || !newPassword) {
      setError("Current and new password are required.");
      render();
      return;
    }
    if (newPassword !== confirm) {
      setError("New password and confirmation do not match.");
      render();
      return;
    }
    if (newPassword.length < 12) {
      setError("New password must be at least 12 characters.");
      render();
      return;
    }
    void (async () => {
      setBusy("password-change");
      setError(null);
      setNotice(null);
      render();
      try {
        const res = await api.changePassword({
          previousPassword,
          newPassword,
        });
        setNotice(res.message ?? "Password updated.");
        currentIn.value = "";
        newIn.value = "";
        confirmIn.value = "";
      } catch (err) {
        setError(errMessage(err));
      } finally {
        setBusy(null);
        render();
      }
    })();
  });

  panel.append(form);
  return panel;
}

/** Admin-only dashboard at /admin. */
function renderAdminShell(): HTMLElement {
  const shell = el("div", { className: "app-shell" });
  const isAdmin = state.user && isAdminRole(state.user.role);

  shell.append(renderTopbar({ adminPage: true }));

  if (!isAdmin) {
    shell.append(
      alertBox(
        "error",
        "You need an owner or admin role to open this page.",
      ),
      el(
        "p",
        { className: "hint" },
        el("a", { href: "/" }, "Back to dashboard"),
      ),
    );
    shell.querySelector('a[href="/"]')?.addEventListener("click", (ev) => {
      ev.preventDefault();
      navigateTo("/");
    });
    return shell;
  }

  if (state.error) shell.append(alertBox("error", state.error));
  if (state.notice) shell.append(alertBox("ok", state.notice));

  // Users stay primary; ribbon sits under them; Invites/Flags open below the ribbon.
  shell.append(adminPageHeader());
  shell.append(adminUsersPanel());
  shell.append(adminRibbon());
  if (state.adminTab === "invites") {
    shell.append(adminInvitesPanel());
  } else if (state.adminTab === "flags") {
    shell.append(adminFlagsPanel());
  }
  return shell;
}

function adminPageHeader(): HTMLElement {
  return el(
    "section",
    { className: "admin-page-intro" },
    el("h1", { className: "admin-page-title" }, "Admin"),
    el(
      "p",
      { className: "sub" },
      "Manage users, invites, and abuse signals. Only owners and admins can open this page.",
    ),
  );
}

/** Horizontal ribbon under the Admin title: Users | Invites | Anomaly flags. */
function adminRibbon(): HTMLElement {
  const nav = el("nav", {
    className: "admin-ribbon",
    role: "tablist",
    "aria-label": "Admin sections",
  });

  const pendingInvites = state.adminInvites.filter(
    (inv) => inv.status === "pending",
  ).length;
  const openFlags = state.adminFlags.filter(
    (f) => String(f.status).toLowerCase() === "open",
  ).length;

  const tabs: Array<{
    id: "users" | "invites" | "flags";
    label: string;
    badge?: string | null;
  }> = [
    {
      id: "users",
      label: "Users",
      badge:
        state.adminUsers.length > 0 ? String(state.adminUsers.length) : null,
    },
    {
      id: "invites",
      label: "Invites",
      badge: pendingInvites > 0 ? String(pendingInvites) : null,
    },
    {
      id: "flags",
      label: "Anomaly flags",
      badge: openFlags > 0 ? String(openFlags) : null,
    },
  ];

  for (const t of tabs) {
    const active = state.adminTab === t.id;
    const btn = el(
      "button",
      {
        type: "button",
        role: "tab",
        className: active ? "admin-ribbon-tab is-active" : "admin-ribbon-tab",
        "aria-selected": active ? "true" : "false",
        id: `admin-tab-${t.id}`,
      },
      t.label,
    );
    if (t.badge) {
      btn.append(
        el(
          "span",
          {
            className: active
              ? "admin-ribbon-badge is-active"
              : "admin-ribbon-badge",
          },
          t.badge,
        ),
      );
    }
    btn.addEventListener("click", () => {
      if (state.adminTab === t.id) return;
      state.adminTab = t.id;
      state.error = null;
      render();
    });
    nav.append(btn);
  }

  return nav;
}

function pendingInvitePanel(): HTMLElement {
  const panel = el("section", { className: "panel invite-pending-panel" });
  panel.append(
    el("h2", null, "Pending invite"),
    el(
      "p",
      { className: "sub" },
      state.invite.preview
        ? `Invite for ${state.invite.preview.email} is ready to accept for this session.`
        : "You have an invite token stored for this browser tab.",
    ),
  );
  if (state.invite.preview) {
    panel.append(inviteBanner());
  }
  const actions = el("div", { className: "form-actions" });
  const acceptBtn = el(
    "button",
    {
      type: "button",
      className: "btn-primary",
      disabled:
        state.busy === "invite-accept" || !state.invite.token
          ? true
          : undefined,
    },
    state.busy === "invite-accept" ? "Accepting…" : "Accept invite",
  ) as HTMLButtonElement;
  acceptBtn.addEventListener("click", () => {
    void (async () => {
      const ok = await tryAcceptPendingInvite();
      if (ok) {
        try {
          await refreshDashboard();
        } catch (err) {
          setError(errMessage(err));
        }
      }
      render();
    })();
  });
  const dismissBtn = el(
    "button",
    { type: "button", className: "btn-ghost" },
    "Dismiss",
  );
  dismissBtn.addEventListener("click", () => {
    clearSessionInviteToken();
    state.invite.token = null;
    state.invite.preview = null;
    render();
  });
  actions.append(acceptBtn, dismissBtn);
  panel.append(actions);
  return panel;
}

function alertBox(kind: "error" | "warn" | "ok" | "info", msg: string): HTMLElement {
  return el("div", { className: `alert alert-${kind}`, role: "alert" }, msg);
}

// ── Devices (paired Macs) ───────────────────────────────────────────────────

function anyMessagesRunningWarning(devices: PublicDevice[]): boolean {
  return devices.some(
    (d) => d.status === "online" && d.messagesRunning === false,
  );
}

function refreshDevicesQuiet(): void {
  void (async () => {
    try {
      const res = await api.listDevices();
      state.devices = res.devices;
      state.accounts = res.accounts;
      // Speed up polling while the Messages-not-running warning is active.
      ensureDevicesPolling(true);
      render();
    } catch {
      // soft-fail — keep showing the stale list rather than erroring the dashboard
    }
  })();
}

/**
 * Start (or retune) the devices list poller.
 * @param retune When true, replace an existing timer if the interval should change.
 */
function ensureDevicesPolling(retune = false): void {
  if (typeof window === "undefined") return;
  const ms = anyMessagesRunningWarning(state.devices)
    ? DEVICES_POLL_MS_WARN
    : DEVICES_POLL_MS;
  if (devicesPollTimer) {
    if (!retune) return;
    clearInterval(devicesPollTimer);
    devicesPollTimer = null;
  }
  devicesPollTimer = setInterval(refreshDevicesQuiet, ms);
}

function stopDevicesPolling(): void {
  if (devicesPollTimer) {
    clearInterval(devicesPollTimer);
    devicesPollTimer = null;
  }
}

function stopPairingWatchers(): void {
  if (pairingPollTimer) {
    clearInterval(pairingPollTimer);
    pairingPollTimer = null;
  }
  if (pairingTickTimer) {
    clearInterval(pairingTickTimer);
    pairingTickTimer = null;
  }
}

function startPairingWatchers(): void {
  stopPairingWatchers();
  if (typeof window === "undefined") return;
  // Re-render every second so the mm:ss countdown ticks down.
  pairingTickTimer = setInterval(() => {
    if (!state.pairing || state.pairingSuccessDevice) return;
    render();
  }, 1000);
  // Poll for the device claiming this code (or an existing device coming online).
  pairingPollTimer = setInterval(() => {
    void pollForPairedDevice();
  }, PAIRING_POLL_MS);
}

async function pollForPairedDevice(): Promise<void> {
  if (!state.pairing || state.pairingSuccessDevice) return;
  if (isExpired(state.pairing.expiresAt)) {
    stopPairingWatchers();
    render();
    return;
  }
  try {
    const res = await api.listDevices();
    const found = res.devices.find((d) => {
      const wasKnown = state.pairingBaselineIds.has(d.id);
      const wasOnline = state.pairingBaselineOnline.has(d.id);
      return !wasKnown || (d.status === "online" && !wasOnline);
    });
    state.devices = res.devices;
    state.accounts = res.accounts;
    if (found) {
      state.pairingSuccessDevice = found.name;
      stopPairingWatchers();
    }
  } catch {
    // transient network error — keep polling until expiry
  }
  render();
}

async function onMintPairingCode(): Promise<void> {
  setBusy("pair");
  setError(null);
  try {
    const pairing = await api.mintPairingCode();
    state.pairing = pairing;
    state.pairingSuccessDevice = null;
    state.pairingBaselineIds = new Set(state.devices.map((d) => d.id));
    state.pairingBaselineOnline = new Set(
      state.devices.filter((d) => d.status === "online").map((d) => d.id),
    );
    startPairingWatchers();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onOpenMessages(d: PublicDevice): Promise<void> {
  setBusy(`open-messages-${d.id}`);
  setError(null);
  try {
    const res = await api.openMessagesOnDevice(d.id, { minimized: true });
    // Optimistic UI — server also patches DDB messagesRunning when true.
    state.devices = state.devices.map((x) =>
      x.id === d.id
        ? { ...x, messagesRunning: res.messagesRunning }
        : x,
    );
    if (res.messagesRunning) {
      setNotice(
        res.launched
          ? `Messages started on “${d.name}” (minimized).`
          : `Messages was already running on “${d.name}”.`,
      );
    } else {
      setError(
        `Asked “${d.name}” to open Messages, but it still isn’t running. Open it manually on that Mac user session.`,
      );
    }
    // Pull fresh device status shortly after.
    window.setTimeout(() => refreshDevicesQuiet(), 1500);
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onRevokeDevice(d: PublicDevice): Promise<void> {
  setBusy(`revoke-device-${d.id}`);
  setError(null);
  try {
    await api.revokeDevice(d.id);
    state.devices = state.devices.filter((x) => x.id !== d.id);
    state.accounts = state.accounts.filter((a) => a.deviceId !== d.id);
    if (state.sendSettingsDeviceId === d.id) {
      state.sendSettingsDeviceId = null;
    }
    setNotice(`${d.name} revoked.`);
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

function devicesPanel(): HTMLElement {
  const panel = el("section", { className: "panel" });
  const headerActions = el("div", { className: "panel-header-actions" });

  const adapter = state.adapterDownload;
  if (adapter?.available && adapter.downloadUrl) {
    const dl = el(
      "a",
      {
        className: "btn-secondary btn-sm",
        href: adapter.downloadUrl,
        download: adapter.filename || "MCPToChat.dmg",
        rel: "noopener",
      },
      adapter.version
        ? `Download Mac adapter (v${adapter.version})`
        : "Download Mac adapter",
    ) as HTMLAnchorElement;
    headerActions.append(dl);
  }

  const pairBtn = el(
    "button",
    {
      type: "button",
      className: "btn-primary btn-sm",
      disabled: state.busy === "pair" ? true : undefined,
    },
    state.busy === "pair" ? "…" : "Pair a Mac",
  ) as HTMLButtonElement;
  pairBtn.addEventListener("click", () => {
    void onMintPairingCode();
  });
  headerActions.append(pairBtn);

  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Devices"),
        el(
          "p",
          { className: "sub" },
          "Macs paired to relay iMessages for MCP tools.",
        ),
      ),
      headerActions,
    ),
  );

  if (adapter?.available && adapter.notes) {
    panel.append(
      el(
        "p",
        { className: "hint device-adapter-hint" },
        adapter.notes,
      ),
    );
  } else if (!adapter?.available) {
    panel.append(
      el(
        "p",
        { className: "hint device-adapter-hint" },
        "Mac adapter download is not published on this server yet. Ask the owner for the latest MCP to Chat.app / DMG.",
      ),
    );
  }

  if (state.pairing) {
    panel.append(pairingCard());
  }

  const list = el("div", { className: "list" });
  if (state.devices.length === 0) {
    list.append(
      el(
        "p",
        { className: "empty" },
        "No devices paired yet. Download the Mac adapter, install it, then click Pair a Mac and enter the code in the menu bar app.",
      ),
    );
  } else {
    for (const d of state.devices) {
      list.append(deviceRow(d, state.devices));
    }
  }
  panel.append(list);
  return panel;
}

function pairingCard(): HTMLElement {
  const pairing = state.pairing!;
  const card = el("div", { className: "pairing-card" });

  if (state.pairingSuccessDevice) {
    card.append(
      alertBox("ok", `${state.pairingSuccessDevice} is online.`),
    );
    const dismissBtn = el(
      "button",
      { type: "button", className: "btn-secondary btn-sm" },
      "Dismiss",
    );
    dismissBtn.addEventListener("click", () => {
      state.pairing = null;
      state.pairingSuccessDevice = null;
      render();
    });
    card.append(el("div", { className: "form-actions" }, dismissBtn));
    return card;
  }

  if (isExpired(pairing.expiresAt)) {
    card.append(
      el(
        "div",
        { className: "pairing-code pairing-code-expired" },
        pairing.code,
      ),
      alertBox("warn", "Code expired — mint a new one."),
    );
    return card;
  }

  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://mcpto.chat";
  card.append(
    el("div", { className: "pairing-code" }, pairing.code),
    el(
      "div",
      { className: "pairing-countdown" },
      `Expires in ${formatCountdown(pairing.expiresAt)}`,
    ),
    el(
      "div",
      { className: "pairing-instructions" },
      el(
        "p",
        { className: "pairing-steps-lede" },
        "On your Mac, open the MCP to Chat menu bar app and enter this code:",
      ),
      el(
        "ol",
        { className: "pairing-steps" },
        el(
          "li",
          null,
          "Click the ",
          el("strong", null, "message"),
          " icon in the menu bar (or launch ",
          el("strong", null, "MCP to Chat"),
          " from Applications).",
        ),
        el(
          "li",
          null,
          "Choose ",
          el("strong", null, "Pair Your Mac…"),
          " (or finish the onboarding wizard to the Pairing step).",
        ),
        el(
          "li",
          null,
          "Paste the code above. Server should be ",
          el("code", null, origin),
          " (default).",
        ),
        el(
          "li",
          null,
          "Click ",
          el("strong", null, "Pair"),
          ". This page updates when the Mac comes online.",
        ),
      ),
    ),
  );
  return card;
}

function deviceRow(d: PublicDevice, allDevices: PublicDevice[]): HTMLElement {
  const expanded = state.sendSettingsDeviceId === d.id;
  const row = el("div", {
    className: expanded ? "device-row device-row-expanded" : "device-row",
  });

  const header = el("div", { className: "device-row-header" });
  const title = el("div", { className: "list-item-title" }, d.name);
  title.append(
    el(
      "span",
      { className: d.status === "online" ? "badge badge-ok" : "badge badge-muted" },
      d.status,
    ),
  );

  const metaParts = [
    d.username ? `user ${d.username}` : null,
    d.hostname ? `host ${d.hostname}` : null,
    `paired ${formatDate(d.createdAt)}`,
    d.lastSeen ? `last seen ${formatDate(d.lastSeen)}` : "never connected",
    d.adapterVersion ? `adapter ${d.adapterVersion}` : null,
  ].filter(Boolean);

  const main = el(
    "div",
    { className: "list-item-main" },
    title,
    el("div", { className: "list-item-meta" }, metaParts.join(" · ")),
  );

  // Handles for the iMessage account(s) paired to this device (plan § A3/A4).
  const handles = state.accounts
    .filter((a) => a.deviceId === d.id)
    .flatMap((a) => a.handles);
  if (handles.length > 0) {
    main.append(
      el("div", { className: "list-item-meta" }, `iMessage: ${handles.join(", ")}`),
    );
  }

  if (d.fda === false) {
    main.append(
      el(
        "div",
        { className: "device-warning" },
        "Full Disk Access not granted — chat.db cannot be read. Re-run the adapter's onboarding wizard.",
      ),
    );
  }
  if (d.automation === false) {
    main.append(
      el(
        "div",
        { className: "device-warning" },
        "Automation (AppleScript) not granted — sends will fail. Re-run the adapter's onboarding wizard.",
      ),
    );
  }
  // Plan § C9/C10 — adapter probes Messages.app via pgrep (per-user).
  // `messagesRunning: false` while online: reads may still work; AppleScript
  // sends need Messages.app open in *this* macOS user session. Status updates
  // ~every 30s from the adapter; SPA re-polls faster while this warning shows.
  if (d.status === "online" && d.messagesRunning === false) {
    const who = d.username ? ` (user “${d.username}”)` : "";
    const seen = d.lastSeen
      ? ` Last checked ${formatDate(d.lastSeen)}.`
      : "";
    const warn = el(
      "div",
      { className: "device-warning device-warning-with-action" },
    );
    warn.append(
      el(
        "span",
        null,
        `Messages.app not detected in this Mac’s login session${who}.${seen} Sends need it running; chat reads may still work.`,
      ),
    );
    const openBtn = el(
      "button",
      {
        type: "button",
        className: "btn-primary btn-sm",
        disabled: state.busy === `open-messages-${d.id}` ? true : undefined,
      },
      state.busy === `open-messages-${d.id}`
        ? "Starting…"
        : "Open Messages (minimized)",
    );
    openBtn.addEventListener("click", () => {
      void onOpenMessages(d);
    });
    warn.append(openBtn);
    main.append(warn);
  }
  const duplicate = findPossibleDuplicateDevice(d, allDevices);
  if (duplicate) {
    main.append(
      el(
        "div",
        { className: "device-warning" },
        `Possible duplicate of "${duplicate.name}" — same Mac user session paired twice. Revoke whichever one is stale.`,
      ),
    );
  }

  const actions = el("div", { className: "list-item-actions" });
  const settingsBtn = el(
    "button",
    {
      type: "button",
      className: expanded ? "btn-primary btn-sm" : "btn-secondary btn-sm",
      "aria-expanded": expanded ? "true" : "false",
      "aria-controls": `send-settings-${d.id}`,
    },
    expanded ? "Hide settings" : "Send settings",
  ) as HTMLButtonElement;
  settingsBtn.addEventListener("click", () => {
    toggleSendSettings(d.id);
  });
  const revokeBtn = el(
    "button",
    {
      type: "button",
      className: "btn-danger btn-sm",
      disabled: state.busy === `revoke-device-${d.id}` ? true : undefined,
    },
    state.busy === `revoke-device-${d.id}` ? "…" : "Revoke",
  ) as HTMLButtonElement;
  revokeBtn.addEventListener("click", () => {
    if (
      !window.confirm(
        `Revoke "${d.name}"? The Mac will stop relaying immediately — any in-progress or future tool calls to this device will fail until it is paired again.`,
      )
    ) {
      return;
    }
    void onRevokeDevice(d);
  });
  actions.append(settingsBtn, revokeBtn);

  header.append(main, actions);
  row.append(header);

  if (expanded) {
    row.append(deviceSendSettingsPanel(d));
  }

  return row;
}

// ── Send settings (D2 confirm-gate) — inline on device card ─────────────────

function toggleSendSettings(deviceId: string): void {
  if (state.sendSettingsDeviceId === deviceId) {
    state.sendSettingsDeviceId = null;
  } else {
    state.sendSettingsDeviceId = deviceId;
    state.profileMenuOpen = false;
  }
  render();
}

function collapseSendSettings(): void {
  state.sendSettingsDeviceId = null;
  render();
}

async function onSaveAccountSettings(
  accountId: string,
  patch: {
    autonomousSends: boolean;
    allowedChatIds: string[];
    allowContactsSends: boolean;
  },
): Promise<void> {
  setBusy(`save-settings-${accountId}`);
  setError(null);
  try {
    const res = await api.updateAccountSettings(accountId, patch);
    state.accounts = state.accounts.map((a) =>
      a.id === accountId ? res.account : a,
    );
    state.accountSaveNotice[accountId] = {
      pushed: res.pushed,
      canonicalized: res.canonicalized,
    };
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

/** Plan § A5 — user-editable account label, saved independently of the send-settings form. */
async function onSaveAccountLabel(accountId: string, label: string): Promise<void> {
  if (!isValidAccountLabel(label)) {
    setError(
      `Label must be 1–${MAX_ACCOUNT_LABEL_LENGTH} characters.`,
    );
    return;
  }
  setBusy(`save-label-${accountId}`);
  setError(null);
  try {
    const res = await api.updateAccountSettings(accountId, { label: label.trim() });
    state.accounts = state.accounts.map((a) => (a.id === accountId ? res.account : a));
    setNotice("Label saved.");
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

/**
 * Inline send-settings panel for one device (accounts + allowlist).
 * Renders inside the device card — not a modal.
 */
function deviceSendSettingsPanel(device: PublicDevice): HTMLElement {
  const accounts = state.accounts.filter((a) => a.deviceId === device.id);
  const panel = el("div", {
    id: `send-settings-${device.id}`,
    className: "device-send-settings",
    role: "region",
    "aria-label": `Send settings for ${device.name}`,
  });

  panel.append(
    el(
      "p",
      { className: "device-send-settings-lede" },
      "Who this AI may message without per-send approval on the Mac.",
    ),
  );

  if (accounts.length === 0) {
    panel.append(
      el(
        "p",
        { className: "hint" },
        "No iMessage account is registered for this Mac yet. Keep the adapter online so handles can sync, then open Send settings again.",
      ),
    );
    return panel;
  }

  for (const a of accounts) {
    panel.append(accountSettingsBlock(a));
  }
  return panel;
}

/**
 * Inline expandable security note under a toggle (fits the card; no popup).
 * Full warning text stays verbatim for the D2 confirm-gate spec.
 */
function securityNoteDetails(text: string): HTMLElement {
  const details = el("details", { className: "security-note" });
  details.append(
    el("summary", { className: "security-note-summary" }, "Security note"),
    el("p", { className: "security-note-body" }, text),
  );
  return details;
}

function accountSettingsBlock(a: PublicAccount): HTMLElement {
  const block = el("div", { className: "settings-block" });

  // Plan § A5 — editable label, separate save action from the send-settings form below.
  const labelId = `account-label-${a.id}`;
  const labelInput = el("input", {
    type: "text",
    id: labelId,
    className: "account-label-input",
    maxlength: String(MAX_ACCOUNT_LABEL_LENGTH),
  }) as HTMLInputElement;
  labelInput.value = a.label;
  const labelSaveBtn = el(
    "button",
    {
      type: "button",
      className: "btn-secondary btn-sm",
      disabled: state.busy === `save-label-${a.id}` ? true : undefined,
    },
    state.busy === `save-label-${a.id}` ? "…" : "Rename",
  ) as HTMLButtonElement;
  labelSaveBtn.addEventListener("click", () => {
    void onSaveAccountLabel(a.id, labelInput.value);
  });
  block.append(
    el("label", { className: "field", for: labelId }, "Account label"),
    el(
      "div",
      { className: "settings-label-row" },
      labelInput,
      labelSaveBtn,
    ),
  );

  const toggleId = `autonomous-sends-${a.id}`;
  const toggle = el("input", {
    type: "checkbox",
    id: toggleId,
    checked: a.settings.autonomousSends ? true : undefined,
  }) as HTMLInputElement;
  block.append(
    el(
      "div",
      { className: "settings-control" },
      el(
        "div",
        { className: "settings-toggle-row" },
        toggle,
        el(
          "label",
          { className: "settings-toggle-label", for: toggleId },
          "Allow autonomous sends (skip approval for all chats)",
        ),
      ),
      securityNoteDetails(AUTONOMOUS_SENDS_WARNING),
    ),
  );

  const contactsId = `allow-contacts-sends-${a.id}`;
  const contactsToggle = el("input", {
    type: "checkbox",
    id: contactsId,
    checked: a.settings.allowContactsSends ? true : undefined,
  }) as HTMLInputElement;
  block.append(
    el(
      "div",
      { className: "settings-control" },
      el(
        "div",
        { className: "settings-toggle-row" },
        contactsToggle,
        el(
          "label",
          { className: "settings-toggle-label", for: contactsId },
          "Allow autonomous sends to people in Contacts",
        ),
      ),
      securityNoteDetails(ALLOW_CONTACTS_SENDS_WARNING),
    ),
  );

  const allowlistId = `allowlist-${a.id}`;
  block.append(
    el("label", { className: "field", for: allowlistId }, "Chat allowlist"),
  );
  const textarea = el("textarea", {
    id: allowlistId,
    className: "allowlist-textarea",
    rows: "4",
    spellcheck: "false",
    autocomplete: "off",
  }) as HTMLTextAreaElement;
  textarea.value = a.settings.allowedChatIds.join("\n");
  block.append(textarea);
  block.append(
    el(
      "p",
      { className: "hint" },
      "One phone number, email, or chat GUID per line. Entries are matched against what the Mac reports — the exact stored form is shown after saving. The allowlist and Contacts options still skip approval even when the global autonomous toggle is off.",
    ),
  );

  const notice = state.accountSaveNotice[a.id];
  if (notice) {
    block.append(
      alertBox(
        "ok",
        notice.pushed
          ? "Saved. Applied immediately."
          : "Saved. The Mac is currently offline — this will take effect the next time it connects.",
      ),
    );
    if (notice.canonicalized.length > 0) {
      const box = el("div", { className: "canonicalized-box" });
      box.append(
        el(
          "p",
          { className: "hint" },
          "Some entries were rewritten to their canonical stored form:",
        ),
      );
      const list = el("ul", { className: "canonicalized-list" });
      for (const line of formatCanonicalizedEntries(notice.canonicalized)) {
        list.append(el("li", null, line));
      }
      box.append(list);
      block.append(box);
    }
  }

  const saveBtn = el(
    "button",
    {
      type: "button",
      className: "btn-primary btn-sm",
      disabled: state.busy === `save-settings-${a.id}` ? true : undefined,
    },
    state.busy === `save-settings-${a.id}` ? "Saving…" : "Save send settings",
  ) as HTMLButtonElement;
  saveBtn.addEventListener("click", () => {
    void onSaveAccountSettings(a.id, {
      autonomousSends: toggle.checked,
      allowedChatIds: parseAllowlistText(textarea.value),
      allowContactsSends: contactsToggle.checked,
    });
  });
  block.append(el("div", { className: "form-actions" }, saveBtn));

  return block;
}

// ── MCP access (connect + active clients + PATs) ───────────────────────────

const SCOPE_HELP: Record<PatScope, string> = {
  read: "Discovery + read tools.",
  send: "Includes send_message (implies read at enforce time).",
  full: "All tools, including account settings changes.",
};

/**
 * Unified MCP panel: server URL front-and-center; setup steps collapsed.
 */
function mcpAccessPanel(): HTMLElement {
  const panel = el("section", { className: "panel" });
  const cfg = mcpSetupConfig();
  const scopesEnforce = state.features["pats.scopesEnforce"] === true;

  panel.append(
    el("h2", null, "MCP access"),
    el(
      "p",
      { className: "sub" },
      "Paste this URL into Claude, Grok, ChatGPT, Cursor, Gemini, or another MCP host.",
    ),
  );

  // Only the server URL is always visible.
  const urlRow = el("div", { className: "mcp-url-row" });
  const urlCode = el("code", { className: "mcp-url-code" }, cfg.mcpResource);
  const copyBtn = el(
    "button",
    {
      type: "button",
      className: "btn-primary btn-sm",
      "data-copy": cfg.mcpResource,
    },
    "Copy",
  );
  copyBtn.addEventListener("click", async () => {
    const ok = await copyText(cfg.mcpResource);
    setNotice(ok ? "MCP URL copied" : "Could not copy");
    render();
  });
  urlRow.append(urlCode, copyBtn);
  panel.append(urlRow);

  const howTo = el("details", { className: "mcp-howto" });
  howTo.append(
    el("summary", null, "How to connect"),
    el(
      "div",
      { className: "mcp-howto-body" },
      el(
        "ol",
        { className: "connect-steps" },
        el(
          "li",
          null,
          "In Claude, Grok, ChatGPT, Cursor, Gemini, or another MCP host, add a ",
          el("strong", null, "custom connector"),
          " / remote MCP server.",
        ),
        el(
          "li",
          null,
          "Use the server URL above (copied). OAuth registration is automatic — no client id or secret to paste.",
        ),
        el(
          "li",
          null,
          "Complete sign-in when prompted",
          cfg.authHostedUiBase
            ? ` (login at ${cfg.authHostedUiBase.replace(/^https?:\/\//, "")}).`
            : " at the hosted login page.",
        ),
      ),
    ),
  );
  panel.append(howTo);

  panel.append(
    el("h3", { className: "panel-h3" }, "Active clients"),
    el(
      "p",
      { className: "hint" },
      "OAuth clients appear after they connect. Personal tokens appear when minted. Disconnect revokes access immediately.",
    ),
  );

  if (state.minted) {
    panel.append(mintedSecretBox(state.minted));
  }

  const list = el("div", { className: "list" });
  const active = state.mcpClients.filter((c) => c.status === "active");
  const inactive = state.mcpClients.filter((c) => c.status !== "active");
  if (active.length === 0 && inactive.length === 0) {
    list.append(
      el(
        "p",
        { className: "empty" },
        "No clients yet. Connect with the server URL above (OAuth discovery), or mint a personal token below. After OAuth, use the connector once so it shows up here.",
      ),
    );
  } else {
    for (const c of active) {
      list.append(mcpClientRow(c));
    }
    for (const c of inactive) {
      list.append(mcpClientRow(c));
    }
  }
  panel.append(list);

  const advanced = el("details", { className: "mcp-advanced" });
  advanced.append(
    el(
      "summary",
      null,
      "Optional: personal access token (stdio bridge / automation)",
    ),
  );
  const advBody = el("div", { className: "mcp-advanced-body" });
  advBody.append(
    el(
      "p",
      { className: "hint" },
      "Use when a client cannot complete browser OAuth. Max 10 active; default TTL 365 days. Token shown once.",
    ),
  );
  if (!scopesEnforce) {
    advBody.append(
      alertBox(
        "info",
        "Scope enforcement is off — scopes are stored but MCP does not reject tools based on them yet.",
      ),
    );
  }

  const form = el("form", { className: "form-stack" });
  const name = el("input", {
    type: "text",
    name: "name",
    required: true,
    placeholder: "e.g. Stdio bridge · laptop",
    maxlength: "128",
    autocomplete: "off",
  }) as HTMLInputElement;
  const days = el("input", {
    type: "number",
    name: "expiresInDays",
    value: "365",
    min: "1",
    max: "3650",
  }) as HTMLInputElement;

  const scopeChecks: Record<PatScope, HTMLInputElement> = {
    read: scopeCheckbox("read", false),
    send: scopeCheckbox("send", false),
    full: scopeCheckbox("full", true),
  };

  const scopeField = el("fieldset", { className: "scope-fieldset" });
  scopeField.append(
    el("legend", null, "Scopes"),
    el(
      "p",
      { className: "hint" },
      "Prefer least privilege. full replaces other scopes.",
    ),
  );
  for (const scope of ["read", "send", "full"] as PatScope[]) {
    const row = el("label", {
      className: "scope-option",
      title: SCOPE_HELP[scope],
    });
    const labelText = el("span", { className: "scope-label" }, scope);
    row.append(scopeChecks[scope], labelText);
    const help = el("span", { className: "scope-help" }, SCOPE_HELP[scope]);
    scopeField.append(el("div", { className: "scope-row" }, row, help));
  }

  const syncExclusive = (changed: PatScope): void => {
    if (changed === "full" && scopeChecks.full.checked) {
      scopeChecks.read.checked = false;
      scopeChecks.send.checked = false;
    } else if (changed !== "full" && scopeChecks[changed].checked) {
      scopeChecks.full.checked = false;
    }
  };
  for (const scope of ["read", "send", "full"] as PatScope[]) {
    scopeChecks[scope].addEventListener("change", () => {
      syncExclusive(scope);
    });
  }

  form.append(
    el(
      "div",
      { className: "form-row" },
      el("label", { className: "field" }, "Client name", name),
      el("label", { className: "field" }, "Expires in days", days),
    ),
    scopeField,
    el(
      "div",
      { className: "form-actions" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "mint" ? true : undefined,
        },
        state.busy === "mint" ? "Creating…" : "Create token",
      ),
    ),
  );

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const n = name.value.trim();
    const d = Number(days.value) || 365;
    const scopes = selectedScopes(scopeChecks);
    if (scopes.length === 0) {
      setError("Select at least one scope (read, send, or full)");
      render();
      return;
    }
    void onMint(n, d, scopes, () => {
      name.value = "";
      scopeChecks.read.checked = false;
      scopeChecks.send.checked = false;
      scopeChecks.full.checked = true;
    });
  });

  advBody.append(form);
  advanced.append(advBody);
  panel.append(advanced);

  return panel;
}

function scopeCheckbox(scope: PatScope, checked: boolean): HTMLInputElement {
  return el("input", {
    type: "checkbox",
    name: "scopes",
    value: scope,
    checked: checked ? true : undefined,
  }) as HTMLInputElement;
}

function selectedScopes(
  checks: Record<PatScope, HTMLInputElement>,
): PatScope[] {
  if (checks.full.checked) return ["full"];
  const out: PatScope[] = [];
  if (checks.read.checked) out.push("read");
  if (checks.send.checked) out.push("send");
  return out;
}

function scopeBadges(scopes: PatScope[]): HTMLElement[] {
  return scopes.map((s) => {
    let cls = "badge badge-muted";
    if (s === "full") cls = "badge badge-ok";
    else if (s === "send") cls = "badge badge-warn";
    else if (s === "read") cls = "badge badge-muted";
    return el("span", { className: cls, title: SCOPE_HELP[s] }, s);
  });
}

function mintedSecretBox(m: MintedPat): HTMLElement {
  const box = el("div", { className: "secret-box" });
  const scopes = scopesOf(m);
  const metaLine = el("div", { className: "list-item-meta" }, `${m.name} · ${m.tokenPrefix}…`);
  for (const b of scopeBadges(scopes)) metaLine.append(b);
  box.append(
    alertBox(
      "warn",
      "Copy this token now. It will not be shown again. Treat it like a password.",
    ),
    metaLine,
    el("code", null, m.token),
    el(
      "div",
      { className: "form-actions" },
      el("button", { type: "button", className: "btn-primary btn-sm" }, "Copy token"),
      el("button", { type: "button", className: "btn-secondary btn-sm" }, "Dismiss"),
    ),
  );
  const [copyBtn, dismissBtn] = box.querySelectorAll("button");
  copyBtn?.addEventListener("click", async () => {
    const ok = await copyText(m.token);
    setNotice(ok ? "Token copied to clipboard" : "Could not copy — select the token manually");
    render();
  });
  dismissBtn?.addEventListener("click", () => {
    state.minted = null;
    setNotice(null);
    render();
  });
  return box;
}

/** Active MCP client row (OAuth or PAT) with disconnect. */
function mcpClientRow(c: McpClient): HTMLElement {
  const row = el("div", { className: "list-item" });
  const revoked = c.status === "revoked";
  const title = el("div", { className: "list-item-title" }, c.name);
  title.append(
    el(
      "span",
      { className: "badge badge-muted" },
      c.kind === "oauth" ? "oauth" : "token",
    ),
  );
  if (revoked) {
    title.append(el("span", { className: "badge badge-err" }, "disconnected"));
  }
  if (c.scopes && c.scopes.length > 0) {
    for (const b of scopeBadges(c.scopes)) title.append(b);
  }
  const metaParts = [
    `created ${formatDate(c.createdAt)}`,
    c.lastUsedAt
      ? `last used ${formatDate(c.lastUsedAt)}`
      : "never used on MCP",
  ];
  if (c.product) metaParts.unshift(c.product);
  row.append(
    el(
      "div",
      { className: "list-item-main" },
      title,
      el("div", { className: "list-item-meta" }, metaParts.join(" · ")),
    ),
  );
  if (!revoked) {
    const actions = el("div", { className: "list-item-actions" });
    const btn = el(
      "button",
      {
        type: "button",
        className: "btn-danger btn-sm",
        disabled: state.busy === `disc-${c.id}` ? true : undefined,
      },
      state.busy === `disc-${c.id}` ? "…" : "Disconnect",
    ) as HTMLButtonElement;
    btn.addEventListener("click", () => {
      const kindLabel = c.kind === "oauth" ? "OAuth client" : "token";
      if (
        !confirm(
          `Disconnect “${c.name}”? This ${kindLabel} will lose MCP access immediately.`,
        )
      ) {
        return;
      }
      void onDisconnectMcpClient(c.id);
    });
    actions.append(btn);
    row.append(actions);
  }
  return row;
}

// ── Audit log (own events) ────────────────────────────────────────────────

function formatMeta(meta: Record<string, unknown> | undefined): string {
  if (!meta || Object.keys(meta).length === 0) return "";
  try {
    return JSON.stringify(meta);
  } catch {
    return "";
  }
}

/**
 * Prefer server-provided accountLabel; fall back to current user's
 * state.accounts label, then a short account id.
 */
function auditAccountDisplay(ev: AuditEvent): {
  text: string;
  title?: string;
} {
  if (!ev.accountId) return { text: "—" };
  const fromApi = ev.accountLabel?.trim();
  if (fromApi) {
    return { text: fromApi, title: ev.accountId };
  }
  const local = state.accounts.find((a) => a.id === ev.accountId);
  if (local?.label?.trim()) {
    return { text: local.label.trim(), title: ev.accountId };
  }
  // Compact id when no label known.
  const id = ev.accountId;
  const short = id.length > 14 ? `${id.slice(0, 12)}…` : id;
  return { text: short, title: id };
}

function auditTableRow(ev: AuditEvent): HTMLElement {
  const tr = el("tr", { className: "audit-tr" });
  const metaStr = formatMeta(ev.meta);
  const account = auditAccountDisplay(ev);
  tr.append(
    el("td", { className: "audit-td audit-td-time" }, formatDate(ev.at)),
    el("td", { className: "audit-td audit-td-action" }, ev.action),
    el("td", { className: "audit-td audit-td-tool" }, ev.tool ?? "—"),
    el(
      "td",
      {
        className: "audit-td audit-td-account",
        title: account.title,
      },
      account.text,
    ),
    el(
      "td",
      {
        className: "audit-td audit-td-meta",
        title: metaStr || undefined,
      },
      metaStr || "—",
    ),
  );
  return tr;
}

/** Full activity log page — always expanded (opened from profile menu). */
function auditPanel(): HTMLElement {
  const panel = el("section", { className: "panel audit-panel" });
  const count = state.auditEvents.length;

  const refreshBtn = el(
    "button",
    {
      type: "button",
      className: "btn-secondary btn-sm",
      disabled: state.busy === "audit-refresh" ? true : undefined,
    },
    state.busy === "audit-refresh" ? "…" : "Refresh",
  ) as HTMLButtonElement;
  refreshBtn.addEventListener("click", () => {
    void onRefreshAudit();
  });

  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Activity log"),
        el(
          "p",
          { className: "sub" },
          count === 0
            ? "Control-plane and MCP events (no message bodies). Newest first."
            : `Your recent events · ${count}${state.auditNextCursor ? "+" : ""} shown. Newest first.`,
        ),
      ),
      el("div", { className: "panel-header-actions" }, refreshBtn),
    ),
  );

  if (state.auditEvents.length === 0) {
    panel.append(
      el(
        "p",
        { className: "empty" },
        "No audit events yet. Pairing devices, minting tokens, and MCP tools will appear here.",
      ),
    );
    return panel;
  }

  const scroll = el("div", {
    className: "audit-table-scroll",
    role: "region",
    "aria-label": "Activity log table",
  });
  const table = el("table", { className: "audit-table" });
  const thead = el("thead");
  thead.append(
    el(
      "tr",
      null,
      el("th", { scope: "col" }, "When"),
      el("th", { scope: "col" }, "Action"),
      el("th", { scope: "col" }, "Tool"),
      el("th", { scope: "col" }, "Account"),
      el("th", { scope: "col" }, "Meta"),
    ),
  );
  const tbody = el("tbody");
  for (const ev of state.auditEvents) {
    tbody.append(auditTableRow(ev));
  }
  table.append(thead, tbody);
  scroll.append(table);
  panel.append(scroll);

  if (state.auditNextCursor) {
    const more = el(
      "div",
      { className: "form-actions", style: "margin-top:0.75rem" },
      el(
        "button",
        {
          type: "button",
          className: "btn-secondary btn-sm",
          disabled: state.busy === "audit-more" ? true : undefined,
        },
        state.busy === "audit-more" ? "Loading…" : "Load more",
      ),
    );
    more.querySelector("button")?.addEventListener("click", () => {
      void onLoadMoreAudit();
    });
    panel.append(more);
  }

  return panel;
}

// ── Admin anomaly flags ───────────────────────────────────────────────────

function severityBadge(severity: string): HTMLElement {
  const s = severity.toLowerCase();
  let cls = "badge badge-muted";
  if (s === "high") cls = "badge badge-err";
  else if (s === "medium") cls = "badge badge-warn";
  else if (s === "low") cls = "badge badge-muted";
  return el("span", { className: cls }, severity);
}

function flagStatusBadge(status: string): HTMLElement {
  const s = status.toLowerCase();
  const cls = s === "open" ? "badge badge-warn" : "badge badge-ok";
  return el("span", { className: cls }, status);
}

function adminFlagRow(f: AdminFlag): HTMLElement {
  const row = el("div", { className: "list-item" });
  const title = el("div", { className: "list-item-title" }, f.reason);
  title.append(severityBadge(String(f.severity)));
  title.append(flagStatusBadge(String(f.status)));

  const metaParts = [
    `id ${f.flagId}`,
    formatDate(f.createdAt),
    f.userId ? `user ${f.userId}` : null,
    f.ackedAt ? `acked ${formatDate(f.ackedAt)}` : null,
  ].filter(Boolean) as string[];
  const metaStr = formatMeta(f.meta);
  if (metaStr) metaParts.push(metaStr);

  const actions = el("div", { className: "list-item-actions" });
  if (String(f.status).toLowerCase() === "open") {
    const ackBtn = el(
      "button",
      {
        type: "button",
        className: "btn-secondary btn-sm",
        disabled: state.busy === `ack-flag-${f.flagId}` ? true : undefined,
      },
      state.busy === `ack-flag-${f.flagId}` ? "…" : "Ack",
    );
    ackBtn.addEventListener("click", () => {
      void onAckFlag(f.flagId);
    });
    actions.append(ackBtn);
  }

  row.append(
    el(
      "div",
      { className: "list-item-main" },
      title,
      el("div", { className: "list-item-meta" }, metaParts.join(" · ")),
    ),
    actions,
  );
  return row;
}

function adminFlagsPanel(): HTMLElement {
  const panel = el("section", {
    className: "panel admin-panel",
    id: "admin-flags",
    role: "tabpanel",
    "aria-labelledby": "admin-tab-flags",
  });
  const anomalyOn = state.features["abuse.anomalyFlags"] === true;

  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Anomaly flags"),
        el(
          "p",
          { className: "sub" },
          "Abuse / rate-limit signals for admins to review. Ack after you look into them.",
        ),
      ),
    ),
  );

  if (!anomalyOn) {
    panel.append(
      alertBox(
        "info",
        "Feature flag abuse.anomalyFlags is off — new flags are not recorded. Existing open flags still list here.",
      ),
    );
  }

  const filters = el("div", { className: "filter-row" });
  for (const opt of [
    { id: "open" as const, label: "Open" },
    { id: "acked" as const, label: "Acked" },
    { id: "all" as const, label: "All" },
  ]) {
    const active = state.adminFlagsStatusFilter === opt.id;
    const btn = el(
      "button",
      {
        type: "button",
        className: active ? "btn-primary btn-sm" : "btn-secondary btn-sm",
        disabled: state.busy === "flags-refresh" ? true : undefined,
      },
      opt.label,
    );
    btn.addEventListener("click", () => {
      if (state.adminFlagsStatusFilter === opt.id) return;
      state.adminFlagsStatusFilter = opt.id;
      void onRefreshFlags();
    });
    filters.append(btn);
  }
  const refreshBtn = el(
    "button",
    {
      type: "button",
      className: "btn-ghost btn-sm",
      disabled: state.busy === "flags-refresh" ? true : undefined,
    },
    state.busy === "flags-refresh" ? "…" : "Refresh",
  );
  refreshBtn.addEventListener("click", () => {
    void onRefreshFlags();
  });
  filters.append(refreshBtn);
  panel.append(filters);

  const list = el("div", { className: "list" });
  if (state.adminFlags.length === 0) {
    list.append(el("p", { className: "empty" }, "No flags match this filter."));
  } else {
    for (const f of state.adminFlags) {
      list.append(adminFlagRow(f));
    }
  }
  panel.append(list);

  if (state.adminFlagsNextCursor) {
    const more = el(
      "div",
      { className: "form-actions", style: "margin-top:0.75rem" },
      el(
        "button",
        {
          type: "button",
          className: "btn-secondary btn-sm",
          disabled: state.busy === "flags-more" ? true : undefined,
        },
        state.busy === "flags-more" ? "Loading…" : "Load more",
      ),
    );
    more.querySelector("button")?.addEventListener("click", () => {
      void onLoadMoreFlags();
    });
    panel.append(more);
  }

  return panel;
}

// ── Admin: invites + users ──────────────────────────────────────────────────

function filteredAdminInvites(): AdminInvite[] {
  const f = state.adminInviteStatusFilter;
  if (f === "all") return state.adminInvites;
  return state.adminInvites.filter((inv) => inv.status === f);
}

function adminInvitesPanel(): HTMLElement {
  const panel = el("section", {
    className: "panel admin-panel",
    id: "admin-invites",
    role: "tabpanel",
    "aria-labelledby": "admin-tab-invites",
  });
  const productized = state.features["invites.productized"] === true;
  const isOwner = state.user?.role === "owner";

  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Invites"),
        el(
          "p",
          { className: "sub" },
          "Send invite links (email + Cognito temp password). Copy the invite URL if SES fails.",
        ),
      ),
    ),
  );

  if (!productized) {
    panel.append(
      alertBox(
        "warn",
        "Feature flag invites.productized is off — enable CONFIG#features in DynamoDB to use in-app invites.",
      ),
    );
    return panel;
  }

  if (state.lastInviteLink) {
    const linkBox = el("div", { className: "invite-url-box" });
    linkBox.append(
      el(
        "p",
        { className: "sub" },
        `One-time invite link for ${state.lastInviteLink.email} (shown once — copy now):`,
      ),
    );
    const urlRow = el("div", { className: "invite-url-row" });
    const urlInput = el("input", {
      type: "text",
      className: "input mono",
      readonly: "true",
      value: state.lastInviteLink.inviteUrl,
    }) as HTMLInputElement;
    const copyBtn = el(
      "button",
      { type: "button", className: "btn-secondary btn-sm" },
      "Copy link",
    );
    copyBtn.addEventListener("click", () => {
      void (async () => {
        const ok = await copyText(state.lastInviteLink!.inviteUrl);
        setNotice(ok ? "Invite link copied." : "Could not copy — select and copy manually.");
        render();
      })();
    });
    const dismissBtn = el(
      "button",
      { type: "button", className: "btn-ghost btn-sm" },
      "Dismiss",
    );
    dismissBtn.addEventListener("click", () => {
      state.lastInviteLink = null;
      render();
    });
    urlRow.append(urlInput, copyBtn, dismissBtn);
    linkBox.append(urlRow);
    panel.append(linkBox);
  }

  // Create form
  const form = el("form", { className: "form admin-invite-form" });
  const email = el("input", {
    type: "email",
    name: "email",
    className: "input",
    placeholder: "tester@example.com",
    required: "true",
    autocomplete: "off",
  }) as HTMLInputElement;
  const role = el("select", {
    name: "role",
    className: "input",
  }) as HTMLSelectElement;
  role.append(
    el("option", { value: "user" }, "user"),
    ...(isOwner ? [el("option", { value: "admin" }, "admin")] : []),
  );
  form.append(
    el(
      "div",
      { className: "form-row" },
      el("label", null, "Email", email),
      el("label", null, "Role", role),
    ),
    el(
      "div",
      { className: "form-actions" },
      el(
        "button",
        {
          type: "submit",
          className: "btn-primary",
          disabled: state.busy === "invite-create" ? true : undefined,
        },
        state.busy === "invite-create" ? "Sending…" : "Send invite",
      ),
    ),
  );
  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void onCreateInvite(email.value.trim(), role.value as "user" | "admin");
  });
  panel.append(form);

  // Status filters
  const filters = el("div", { className: "filter-row" });
  for (const opt of [
    { id: "pending" as const, label: "Pending" },
    { id: "accepted" as const, label: "Accepted" },
    { id: "revoked" as const, label: "Revoked" },
    { id: "all" as const, label: "All" },
  ]) {
    const active = state.adminInviteStatusFilter === opt.id;
    const btn = el(
      "button",
      {
        type: "button",
        className: active ? "btn-primary btn-sm" : "btn-secondary btn-sm",
        disabled: state.busy === "invites-refresh" ? true : undefined,
      },
      opt.label,
    );
    btn.addEventListener("click", () => {
      if (state.adminInviteStatusFilter === opt.id) return;
      state.adminInviteStatusFilter = opt.id;
      render();
    });
    filters.append(btn);
  }
  const refreshBtn = el(
    "button",
    {
      type: "button",
      className: "btn-ghost btn-sm",
      disabled: state.busy === "invites-refresh" ? true : undefined,
    },
    state.busy === "invites-refresh" ? "…" : "Refresh",
  );
  refreshBtn.addEventListener("click", () => {
    void onRefreshAdminInvites();
  });
  filters.append(refreshBtn);
  panel.append(filters);

  const list = el("div", { className: "list" });
  const rows = filteredAdminInvites();
  if (rows.length === 0) {
    list.append(el("p", { className: "empty" }, "No invites match this filter."));
  } else {
    for (const inv of rows) {
      list.append(adminInviteRow(inv));
    }
  }
  panel.append(list);
  return panel;
}

function adminInviteRow(inv: AdminInvite): HTMLElement {
  const row = el("div", { className: "list-row admin-invite-row" });
  const pending = inv.status === "pending";
  const meta = el("div", { className: "list-main" });
  const statusBadgeClass =
    inv.status === "pending"
      ? "badge badge-warn"
      : inv.status === "accepted"
        ? "badge badge-ok"
        : inv.status === "revoked" || inv.status === "expired"
          ? "badge badge-err"
          : "badge badge-muted";
  meta.append(
    el(
      "div",
      { className: "list-title" },
      inv.email,
      " ",
      el("span", { className: statusBadgeClass }, inv.status),
      " ",
      el("span", { className: "badge badge-muted" }, inv.role),
    ),
    el(
      "div",
      { className: "list-meta" },
      `Created ${formatDate(inv.createdAt)} · expires ${formatDate(inv.expiresAt)}`,
      inv.resendCount > 0 ? ` · resent ×${inv.resendCount}` : "",
      inv.acceptedAt ? ` · accepted ${formatDate(inv.acceptedAt)}` : "",
    ),
  );
  row.append(meta);

  if (pending) {
    const actions = el("div", { className: "row-actions" });
    const resendBtn = el(
      "button",
      {
        type: "button",
        className: "btn-secondary btn-sm",
        disabled: state.busy === `invite-resend-${inv.id}` ? true : undefined,
      },
      state.busy === `invite-resend-${inv.id}` ? "…" : "Resend",
    );
    resendBtn.addEventListener("click", () => {
      void onResendInvite(inv.id);
    });
    const revokeBtn = el(
      "button",
      {
        type: "button",
        className: "btn-danger btn-sm",
        disabled: state.busy === `invite-revoke-${inv.id}` ? true : undefined,
      },
      state.busy === `invite-revoke-${inv.id}` ? "…" : "Revoke",
    );
    revokeBtn.addEventListener("click", () => {
      if (
        !window.confirm(
          `Revoke invite for ${inv.email}? They will not be able to accept this link.`,
        )
      ) {
        return;
      }
      void onRevokeInvite(inv.id);
    });
    actions.append(resendBtn, revokeBtn);
    row.append(actions);
  }
  return row;
}

function filteredAdminUsers(): AdminUser[] {
  const q = state.adminUserQuery.trim().toLowerCase();
  const role = state.adminUserRoleFilter;
  const status = state.adminUserStatusFilter;
  return state.adminUsers.filter((u) => {
    if (role !== "all" && u.role !== role) return false;
    if (status !== "all" && u.status !== status) return false;
    if (q) {
      const email = u.email.toLowerCase();
      const id = u.userId.toLowerCase();
      if (!email.includes(q) && !id.includes(q)) return false;
    }
    return true;
  });
}

function adminUsersPanel(): HTMLElement {
  const panel = el("section", {
    className: "panel admin-panel",
    id: "admin-users",
  });
  const isOwner = state.user?.role === "owner";
  const rows = filteredAdminUsers();

  const headerActions = el("div", { className: "row-actions" });
  const exportBtn = el(
    "button",
    {
      type: "button",
      className: "btn-secondary btn-sm",
      disabled: state.busy === "users-export" ? true : undefined,
    },
    state.busy === "users-export" ? "…" : "Export CSV",
  );
  exportBtn.addEventListener("click", () => {
    void onExportAdminUsers();
  });
  const refreshBtn = el(
    "button",
    {
      type: "button",
      className: "btn-ghost btn-sm",
      disabled: state.busy === "users-refresh" ? true : undefined,
    },
    state.busy === "users-refresh" ? "…" : "Refresh",
  );
  refreshBtn.addEventListener("click", () => {
    void onRefreshAdminUsers();
  });
  headerActions.append(exportBtn, refreshBtn);

  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h2", null, "Users"),
        el(
          "p",
          { className: "sub" },
          "Search, filter, view activity, disable/enable, revoke PATs, reset password, delete, or (owner) change roles.",
        ),
      ),
      headerActions,
    ),
  );

  // Search + filters (client-side on loaded list; Refresh re-queries server with same filters).
  const filters = el("div", { className: "filter-row admin-user-filters" });
  const search = el("input", {
    type: "search",
    className: "input",
    placeholder: "Search email or user id…",
    value: state.adminUserQuery,
    autocomplete: "off",
    "aria-label": "Search users",
  }) as HTMLInputElement;
  search.addEventListener("input", () => {
    state.adminUserQuery = search.value;
    render();
  });
  search.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter") {
      ev.preventDefault();
      void onRefreshAdminUsers();
    }
  });

  const roleSel = el("select", {
    className: "input input-sm",
    "aria-label": "Filter by role",
  }) as HTMLSelectElement;
  for (const opt of [
    { id: "all" as const, label: "All roles" },
    { id: "owner" as const, label: "owner" },
    { id: "admin" as const, label: "admin" },
    { id: "user" as const, label: "user" },
  ]) {
    const o = el("option", { value: opt.id }, opt.label) as HTMLOptionElement;
    if (state.adminUserRoleFilter === opt.id) o.selected = true;
    roleSel.append(o);
  }
  roleSel.addEventListener("change", () => {
    state.adminUserRoleFilter = roleSel.value as UserRole | "all";
    render();
  });

  const statusSel = el("select", {
    className: "input input-sm",
    "aria-label": "Filter by status",
  }) as HTMLSelectElement;
  for (const opt of [
    { id: "all" as const, label: "All statuses" },
    { id: "active" as const, label: "active" },
    { id: "disabled" as const, label: "disabled" },
  ]) {
    const o = el("option", { value: opt.id }, opt.label) as HTMLOptionElement;
    if (state.adminUserStatusFilter === opt.id) o.selected = true;
    statusSel.append(o);
  }
  statusSel.addEventListener("change", () => {
    state.adminUserStatusFilter = statusSel.value as
      | "active"
      | "disabled"
      | "all";
    render();
  });

  filters.append(search, roleSel, statusSel);
  panel.append(filters);

  const countHint = el(
    "p",
    { className: "hint" },
    state.adminUsers.length === 0
      ? "No users loaded."
      : `Showing ${rows.length} of ${state.adminUsers.length} users.`,
  );
  panel.append(countHint);

  const list = el("div", { className: "list" });
  if (state.adminUsers.length === 0) {
    list.append(el("p", { className: "empty" }, "No users yet."));
  } else if (rows.length === 0) {
    list.append(
      el("p", { className: "empty" }, "No users match this search/filter."),
    );
  } else {
    for (const u of rows) {
      list.append(adminUserRow(u, isOwner));
    }
  }
  panel.append(list);

  if (state.adminUserActivity) {
    panel.append(adminUserActivityPanel());
  }

  return panel;
}

function adminUserActivityPanel(): HTMLElement {
  const act = state.adminUserActivity!;
  const panel = el("div", {
    className: "admin-user-activity",
    id: "admin-user-activity",
  });

  const closeBtn = el(
    "button",
    { type: "button", className: "btn-ghost btn-sm" },
    "Close",
  );
  closeBtn.addEventListener("click", () => {
    state.adminUserActivity = null;
    render();
  });

  const refreshBtn = el(
    "button",
    {
      type: "button",
      className: "btn-secondary btn-sm",
      disabled: state.busy === "user-audit-refresh" ? true : undefined,
    },
    state.busy === "user-audit-refresh" ? "…" : "Refresh",
  );
  refreshBtn.addEventListener("click", () => {
    void onLoadUserActivity(act.userId, act.email);
  });

  panel.append(
    el(
      "div",
      { className: "panel-header" },
      el(
        "div",
        null,
        el("h3", null, `Activity · ${act.email}`),
        el(
          "p",
          { className: "sub" },
          `${act.events.length}${act.nextCursor ? "+" : ""} events · newest first · no message bodies`,
        ),
      ),
      el("div", { className: "row-actions" }, refreshBtn, closeBtn),
    ),
  );

  if (act.events.length === 0) {
    panel.append(
      el("p", { className: "empty" }, "No audit events for this user yet."),
    );
    return panel;
  }

  const scroll = el("div", {
    className: "audit-table-scroll",
    role: "region",
    "aria-label": `Activity for ${act.email}`,
  });
  const table = el("table", { className: "audit-table" });
  const thead = el("thead");
  thead.append(
    el(
      "tr",
      null,
      el("th", { scope: "col" }, "When"),
      el("th", { scope: "col" }, "Action"),
      el("th", { scope: "col" }, "Tool"),
      el("th", { scope: "col" }, "Account"),
      el("th", { scope: "col" }, "Meta"),
    ),
  );
  const tbody = el("tbody");
  for (const ev of act.events) {
    tbody.append(auditTableRow(ev));
  }
  table.append(thead, tbody);
  scroll.append(table);
  panel.append(scroll);

  if (act.nextCursor) {
    const more = el(
      "div",
      { className: "form-actions", style: "margin-top:0.75rem" },
      el(
        "button",
        {
          type: "button",
          className: "btn-secondary btn-sm",
          disabled: state.busy === "user-audit-more" ? true : undefined,
        },
        state.busy === "user-audit-more" ? "Loading…" : "Load more",
      ),
    );
    more.querySelector("button")?.addEventListener("click", () => {
      void onLoadMoreUserActivity();
    });
    panel.append(more);
  }

  return panel;
}

/** Inline SVG for icon buttons (24×24 viewBox, currentColor stroke). */
function adminIconSvg(paths: string, opts?: { filled?: boolean }): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "16");
  svg.setAttribute("height", "16");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  svg.classList.add("btn-icon-svg");
  if (opts?.filled) {
    svg.innerHTML = paths;
  } else {
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2");
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");
    svg.innerHTML = paths;
  }
  return svg;
}

const ADMIN_ICONS = {
  activity: `<path d="M4 19V5"/><path d="M10 19V9"/><path d="M16 19v-6"/><path d="M22 19V8"/>`,
  key: `<path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>`,
  lockReset: `<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 7.5-1.9"/><path d="M17 4v3h-3"/>`,
  ban: `<circle cx="12" cy="12" r="9"/><path d="M5.5 5.5l13 13"/>`,
  check: `<path d="M20 6L9 17l-5-5"/>`,
  trash: `<path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/>`,
} as const;

function adminIconButton(opts: {
  label: string;
  icon: keyof typeof ADMIN_ICONS;
  onClick: () => void;
  busy?: boolean;
  disabled?: boolean;
  danger?: boolean;
  active?: boolean;
}): HTMLButtonElement {
  const classes = [
    "btn-icon",
    opts.danger ? "btn-icon-danger" : "btn-icon-secondary",
    opts.active ? "is-active" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const btn = el("button", {
    type: "button",
    className: classes,
    title: opts.label,
    "aria-label": opts.label,
    disabled: opts.disabled || opts.busy ? true : undefined,
  }) as HTMLButtonElement;
  if (opts.busy) {
    btn.textContent = "…";
  } else {
    btn.append(adminIconSvg(ADMIN_ICONS[opts.icon]));
  }
  btn.addEventListener("click", () => {
    opts.onClick();
  });
  return btn;
}

function adminUserRow(u: AdminUser, isOwner: boolean): HTMLElement {
  const row = el("div", { className: "list-row admin-user-row" });
  const self = state.user?.userId === u.userId;
  const active = u.status === "active";

  const meta = el("div", { className: "list-main" });
  meta.append(
    el(
      "div",
      { className: "list-title" },
      u.email,
      self ? el("span", { className: "badge" }, "you") : null,
      " ",
      el(
        "span",
        {
          className: active ? "badge badge-ok" : "badge badge-err",
        },
        u.status,
      ),
      " ",
      el("span", { className: "badge badge-muted" }, u.role),
    ),
    el(
      "div",
      { className: "list-meta" },
      `Joined ${formatDate(u.createdAt)} · ${u.userId.slice(0, 12)}…`,
    ),
  );
  row.append(meta);

  const actions = el("div", { className: "row-actions admin-user-actions" });

  actions.append(
    adminIconButton({
      label:
        state.adminUserActivity?.userId === u.userId
          ? "Hide activity"
          : "View activity",
      icon: "activity",
      busy: state.busy === `user-audit-${u.userId}`,
      active: state.adminUserActivity?.userId === u.userId,
      onClick: () => {
        if (state.adminUserActivity?.userId === u.userId) {
          state.adminUserActivity = null;
          render();
          return;
        }
        void onLoadUserActivity(u.userId, u.email);
      },
    }),
  );

  if (isOwner) {
    const roleWrap = el("label", {
      className: "admin-role-select-wrap",
      title: "Change role",
    });
    const roleSelect = el("select", {
      className: "input input-sm admin-role-select",
      "aria-label": `Change role for ${u.email}`,
      title: "Change role",
      disabled: state.busy === `user-role-${u.userId}` ? true : undefined,
    }) as HTMLSelectElement;
    for (const r of ["owner", "admin", "user"] as UserRole[]) {
      const opt = el("option", { value: r }, r) as HTMLOptionElement;
      if (r === u.role) opt.selected = true;
      roleSelect.append(opt);
    }
    roleSelect.addEventListener("change", () => {
      const next = roleSelect.value as UserRole;
      if (next === u.role) return;
      if (!window.confirm(`Change role for ${u.email} to ${next}?`)) {
        roleSelect.value = u.role;
        return;
      }
      void onPatchUserRole(u.userId, next);
    });
    roleWrap.append(roleSelect);
    actions.append(roleWrap);
  }

  actions.append(
    adminIconButton({
      label: "Revoke all PATs",
      icon: "key",
      busy: state.busy === `user-pats-${u.userId}`,
      onClick: () => {
        if (!window.confirm(`Revoke all PATs for ${u.email}?`)) return;
        void onRevokeUserPats(u.userId);
      },
    }),
    adminIconButton({
      label: "Reset password (email recovery code)",
      icon: "lockReset",
      busy: state.busy === `user-reset-${u.userId}`,
      onClick: () => {
        if (
          !window.confirm(
            `Send a Cognito password-reset email to ${u.email}?`,
          )
        ) {
          return;
        }
        void onResetUserPassword(u.userId);
      },
    }),
  );

  if (active) {
    actions.append(
      adminIconButton({
        label: "Disable account",
        icon: "ban",
        danger: true,
        disabled: self,
        busy: state.busy === `user-disable-${u.userId}`,
        onClick: () => {
          if (
            !window.confirm(
              `Disable ${u.email}? They lose API/MCP access until re-enabled.`,
            )
          ) {
            return;
          }
          void onDisableUser(u.userId);
        },
      }),
    );
  } else {
    actions.append(
      adminIconButton({
        label: "Enable account",
        icon: "check",
        busy: state.busy === `user-enable-${u.userId}`,
        onClick: () => {
          void onEnableUser(u.userId);
        },
      }),
    );
  }

  if (!self) {
    actions.append(
      adminIconButton({
        label: "Delete user permanently",
        icon: "trash",
        danger: true,
        busy: state.busy === `user-delete-${u.userId}`,
        onClick: () => {
          if (
            !window.confirm(
              `Permanently delete ${u.email}? This removes their Cognito user, profile, and allowlist. Devices/accounts may remain as orphans.`,
            )
          ) {
            return;
          }
          void onDeleteUser(u.userId);
        },
      }),
    );
  }

  row.append(actions);
  return row;
}

// ── Actions ───────────────────────────────────────────────────────────────

async function handleLoginResult(
  res: Awaited<ReturnType<typeof api.login>>,
  email: string,
): Promise<void> {
  if (isLoginChallenge(res)) {
    const name = res.challenge.name;
    if (name === "NEW_PASSWORD_REQUIRED") {
      state.challenge = {
        kind: "NEW_PASSWORD_REQUIRED",
        session: res.challenge.session,
        email,
      };
      setError(null);
      return;
    }
    if (name === "SOFTWARE_TOKEN_MFA") {
      state.challenge = {
        kind: "SOFTWARE_TOKEN_MFA",
        session: res.challenge.session,
        email,
      };
      setError(null);
      return;
    }
    setError(`Unsupported challenge: ${name}`);
    return;
  }

  state.challenge = null;
  state.user = res.user;
  setError(null);
  setNotice(null);
  try {
    // Bind invite after session is established (JWT sub + email match).
    if (state.invite.token || readSessionInviteToken()) {
      await tryAcceptPendingInvite();
    }
    await refreshDashboard();
  } catch (err) {
    setError(errMessage(err));
  }
}

async function onForgotPasswordRequest(email: string): Promise<void> {
  if (!email) {
    setError("Email is required");
    render();
    return;
  }
  setBusy("forgot");
  setError(null);
  try {
    const res = await api.forgotPassword(email);
    state.forgotPassword = { step: "confirm", email };
    setNotice(
      res.message ??
        "If an account exists for that email, we sent a verification code.",
    );
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onForgotPasswordConfirm(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  if (!email || !code || !newPassword) {
    setError("Email, code, and new password are required");
    render();
    return;
  }
  setBusy("forgot");
  setError(null);
  try {
    const res = await api.confirmForgotPassword({ email, code, newPassword });
    state.forgotPassword = null;
    setNotice(
      res.message ?? "Password updated. Sign in with your new password.",
    );
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onLogin(email: string, password: string): Promise<void> {
  if (!email) {
    setError("Email is required");
    render();
    return;
  }
  setBusy("login");
  setError(null);
  try {
    const res = await api.login(email, password);
    await handleLoginResult(res, email);
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onNewPassword(
  email: string,
  session: string,
  newPassword: string,
): Promise<void> {
  setBusy("login");
  setError(null);
  try {
    const res = await api.respondNewPassword({ session, email, newPassword });
    await handleLoginResult(res, email);
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onTotp(
  email: string,
  session: string,
  totpCode: string,
): Promise<void> {
  setBusy("login");
  setError(null);
  try {
    const res = await api.respondTotp({ session, email, totpCode });
    await handleLoginResult(res, email);
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onLogout(): Promise<void> {
  setBusy("logout");
  try {
    await api.logout();
  } catch {
    // Logout is idempotent; clear local state anyway
  } finally {
    stopDevicesPolling();
    stopPairingWatchers();
    state.user = null;
    state.devices = [];
    state.accounts = [];
    state.pairing = null;
    state.pairingSuccessDevice = null;
    state.accountSaveNotice = {};
    state.tokens = [];
    state.mcpClients = [];
    state.minted = null;
    state.features = { ...DEFAULT_FEATURE_FLAGS };
    state.auditEvents = [];
    state.auditNextCursor = null;
    state.auditExpanded = false;
    if (typeof window !== "undefined" && isAdminPath()) {
      window.history.replaceState({}, "", "/");
    }
    state.adminFlags = [];
    state.adminFlagsNextCursor = null;
    state.adminFlagsStatusFilter = "open";
    state.adminInvites = [];
    state.adminUsers = [];
    state.lastInviteLink = null;
    state.challenge = null;
    setBusy(null);
    setError(null);
    setNotice(null);
  }
}

async function onMint(
  name: string,
  expiresInDays: number,
  scopes: PatScope[],
  clearForm: () => void,
): Promise<void> {
  if (!name) {
    setError("Token name is required");
    render();
    return;
  }
  if (scopes.length === 0) {
    setError("Select at least one scope (read, send, or full)");
    render();
    return;
  }
  setBusy("mint");
  setError(null);
  setNotice(null);
  try {
    const minted = await api.mintToken({ name, expiresInDays, scopes });
    // Prefer server scopes; fall back to what we requested if older server omits them.
    const effectiveScopes =
      Array.isArray(minted.scopes) && minted.scopes.length > 0
        ? scopesOf(minted)
        : scopes;
    state.minted = { ...minted, scopes: effectiveScopes };
    // Add metadata to list (without raw token)
    const meta: PatMeta = {
      id: minted.id,
      name: minted.name,
      tokenPrefix: minted.tokenPrefix,
      createdAt: minted.createdAt,
      expiresAt: minted.expiresAt,
      scopes: effectiveScopes,
    };
    state.tokens = [meta, ...state.tokens.filter((t) => t.id !== meta.id)];
    // Minted PATs show in Active clients via merge (pat_{id}) even before first /mcp use.
    const patClientId = `pat_${minted.id}`;
    const patClient: McpClient = {
      id: patClientId,
      kind: "pat",
      name: minted.name,
      status: "active",
      createdAt: minted.createdAt,
      lastUsedAt: minted.createdAt,
      scopes: effectiveScopes,
      patTokenId: minted.id,
    };
    state.mcpClients = [
      patClient,
      ...state.mcpClients.filter((c) => c.id !== patClientId),
    ];
    clearForm();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onDisconnectMcpClient(clientId: string): Promise<void> {
  setBusy(`disc-${clientId}`);
  setError(null);
  try {
    await api.disconnectMcpClient(clientId);
    const wasPat = clientId.startsWith("pat_");
    const patTokenId = wasPat ? clientId.slice("pat_".length) : undefined;
    state.mcpClients = state.mcpClients.map((c) =>
      c.id === clientId ? { ...c, status: "revoked" as const } : c,
    );
    // Drop revoked PATs from the tokens list and minted secret if present.
    if (patTokenId) {
      state.tokens = state.tokens.filter((t) => t.id !== patTokenId);
      if (state.minted?.id === patTokenId) state.minted = null;
      // Remove PAT row entirely (revoked tokens are deleted server-side).
      state.mcpClients = state.mcpClients.filter((c) => c.id !== clientId);
    }
    setNotice("Client disconnected");
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onRefreshAudit(): Promise<void> {
  setBusy("audit-refresh");
  setError(null);
  try {
    const page = await api.listAudit({ limit: AUDIT_PAGE_SIZE });
    state.auditEvents = page.events;
    state.auditNextCursor = page.nextCursor ?? null;
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onLoadMoreAudit(): Promise<void> {
  if (!state.auditNextCursor) return;
  setBusy("audit-more");
  setError(null);
  try {
    const page = await api.listAudit({
      limit: AUDIT_PAGE_SIZE,
      cursor: state.auditNextCursor,
    });
    const seen = new Set(state.auditEvents.map((e) => e.id));
    for (const ev of page.events) {
      if (!seen.has(ev.id)) {
        state.auditEvents.push(ev);
        seen.add(ev.id);
      }
    }
    state.auditNextCursor = page.nextCursor ?? null;
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onRefreshFlags(): Promise<void> {
  if (!state.user || !isAdminRole(state.user.role)) return;
  setBusy("flags-refresh");
  setError(null);
  try {
    const status =
      state.adminFlagsStatusFilter === "all"
        ? undefined
        : state.adminFlagsStatusFilter;
    const page = await api.listAdminFlags({
      limit: FLAGS_PAGE_SIZE,
      ...(status ? { status } : {}),
    });
    state.adminFlags = page.flags;
    state.adminFlagsNextCursor = page.nextCursor ?? null;
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onLoadMoreFlags(): Promise<void> {
  if (!state.adminFlagsNextCursor) return;
  if (!state.user || !isAdminRole(state.user.role)) return;
  setBusy("flags-more");
  setError(null);
  try {
    const status =
      state.adminFlagsStatusFilter === "all"
        ? undefined
        : state.adminFlagsStatusFilter;
    const page = await api.listAdminFlags({
      limit: FLAGS_PAGE_SIZE,
      cursor: state.adminFlagsNextCursor,
      ...(status ? { status } : {}),
    });
    const seen = new Set(state.adminFlags.map((f) => f.flagId));
    for (const f of page.flags) {
      if (!seen.has(f.flagId)) {
        state.adminFlags.push(f);
        seen.add(f.flagId);
      }
    }
    state.adminFlagsNextCursor = page.nextCursor ?? null;
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onAckFlag(flagId: string): Promise<void> {
  setBusy(`ack-flag-${flagId}`);
  setError(null);
  try {
    const updated = await api.ackAdminFlag(flagId);
    state.adminFlags = state.adminFlags.map((f) =>
      f.flagId === flagId ? updated : f,
    );
    // If filtering open only, drop acked from list.
    if (state.adminFlagsStatusFilter === "open") {
      state.adminFlags = state.adminFlags.filter(
        (f) => String(f.status).toLowerCase() === "open",
      );
    }
    setNotice(`Flag ${flagId} acknowledged`);
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
  }
}

async function onCreateInvite(
  email: string,
  role: "user" | "admin",
): Promise<void> {
  if (!email) {
    setError("Email is required");
    render();
    return;
  }
  setBusy("invite-create");
  setError(null);
  try {
    const created = await api.createAdminInvite({ email, role });
    state.lastInviteLink = {
      email: created.email,
      inviteUrl: created.inviteUrl,
    };
    const ses = created.emailDelivery?.ses ?? "unknown";
    setNotice(
      `Invite created for ${created.email} (SES: ${ses}). Copy the one-time link below if needed.`,
    );
    state.adminInvites = await api.listAdminInvites();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onResendInvite(inviteId: string): Promise<void> {
  setBusy(`invite-resend-${inviteId}`);
  setError(null);
  try {
    const created = await api.resendAdminInvite(inviteId);
    state.lastInviteLink = {
      email: created.email,
      inviteUrl: created.inviteUrl,
    };
    setNotice(`Invite resent to ${created.email}. New link shown once below.`);
    state.adminInvites = await api.listAdminInvites();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onRevokeInvite(inviteId: string): Promise<void> {
  setBusy(`invite-revoke-${inviteId}`);
  setError(null);
  try {
    await api.revokeAdminInvite(inviteId);
    setNotice("Invite revoked.");
    state.adminInvites = await api.listAdminInvites();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onRefreshAdminInvites(): Promise<void> {
  setBusy("invites-refresh");
  setError(null);
  try {
    state.adminInvites = await api.listAdminInvites();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onRefreshAdminUsers(): Promise<void> {
  setBusy("users-refresh");
  setError(null);
  try {
    // Full list from server; search/role/status filters apply client-side
    // for instant typing. API still supports ?q=&role=&status= for large lists.
    state.adminUsers = await api.listAdminUsers();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onLoadUserActivity(
  userId: string,
  email: string,
): Promise<void> {
  setBusy(`user-audit-${userId}`);
  setError(null);
  try {
    const page = await api.listAdminUserAudit(userId, {
      limit: AUDIT_PAGE_SIZE,
    });
    state.adminUserActivity = {
      userId: page.userId,
      email: page.email || email,
      events: page.events,
      nextCursor: page.nextCursor ?? null,
    };
    // Scroll activity into view after paint.
    queueMicrotask(() => {
      document
        .getElementById("admin-user-activity")
        ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onLoadMoreUserActivity(): Promise<void> {
  const act = state.adminUserActivity;
  if (!act?.nextCursor) return;
  setBusy("user-audit-more");
  setError(null);
  try {
    const page = await api.listAdminUserAudit(act.userId, {
      limit: AUDIT_PAGE_SIZE,
      cursor: act.nextCursor,
    });
    const seen = new Set(act.events.map((e) => e.id));
    for (const ev of page.events) {
      if (!seen.has(ev.id)) {
        act.events.push(ev);
        seen.add(ev.id);
      }
    }
    act.nextCursor = page.nextCursor ?? null;
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onDisableUser(userId: string): Promise<void> {
  setBusy(`user-disable-${userId}`);
  setError(null);
  try {
    await api.disableAdminUser(userId);
    setNotice("User disabled.");
    state.adminUsers = await api.listAdminUsers();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onEnableUser(userId: string): Promise<void> {
  setBusy(`user-enable-${userId}`);
  setError(null);
  try {
    await api.enableAdminUser(userId);
    setNotice("User enabled.");
    state.adminUsers = await api.listAdminUsers();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onRevokeUserPats(userId: string): Promise<void> {
  setBusy(`user-pats-${userId}`);
  setError(null);
  try {
    const res = await api.revokeAdminUserPats(userId);
    setNotice(`PATs revoked (tokenVersion ${res.tokenVersion}).`);
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onPatchUserRole(userId: string, role: UserRole): Promise<void> {
  setBusy(`user-role-${userId}`);
  setError(null);
  try {
    await api.patchAdminUserRole(userId, role);
    setNotice(`Role updated to ${role}.`);
    state.adminUsers = await api.listAdminUsers();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onResetUserPassword(userId: string): Promise<void> {
  setBusy(`user-reset-${userId}`);
  setError(null);
  try {
    const res = await api.resetAdminUserPassword(userId);
    setNotice(res.message ?? "Password reset email sent.");
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onDeleteUser(userId: string): Promise<void> {
  setBusy(`user-delete-${userId}`);
  setError(null);
  try {
    await api.deleteAdminUser(userId);
    setNotice("User deleted.");
    state.adminUsers = await api.listAdminUsers();
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onExportAdminUsers(): Promise<void> {
  setBusy("users-export");
  setError(null);
  try {
    await api.exportAdminUsersCsv();
    setNotice("Users CSV downloaded.");
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onSignUp(email: string, password: string): Promise<void> {
  setBusy("signup");
  setError(null);
  setNotice(null);
  try {
    const res = await api.signUp(email, password);
    if (res.status === "confirmed") {
      state.signUp = null;
      setNotice(res.message ?? "Account created. Sign in.");
    } else {
      state.signUp = { step: "confirm", email: res.email ?? email };
      setNotice(res.message ?? "Check your email for a verification code.");
    }
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onConfirmSignUp(email: string, code: string): Promise<void> {
  setBusy("signup-confirm");
  setError(null);
  try {
    const res = await api.confirmSignUp({ email, code });
    state.signUp = null;
    setNotice(res.message ?? "Email confirmed. Sign in.");
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}

async function onResendConfirmation(email: string): Promise<void> {
  setBusy("signup-resend");
  setError(null);
  try {
    const res = await api.resendConfirmation(email);
    setNotice(res.message ?? "If pending, a new code was sent.");
  } catch (err) {
    setError(errMessage(err));
  } finally {
    setBusy(null);
    render();
  }
}
